import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import { 
  Calendar, Bell, Wallet, Plus, FileText, Settings, LogOut, User, CreditCard, 
  TrendingUp, TrendingDown, AlertCircle, CheckCircle, Clock, Trash2, Edit, Upload, X, Save, 
  ChevronLeft, ChevronRight, Home, Repeat, DollarSign, PieChart, Search, Menu, 
  XCircle, Loader2, Copy, Image, ShoppingCart, Car, Heart, GraduationCap, 
  Gamepad2, Tv, Smartphone, Wifi, Zap, Droplet, Flame, Building, Coffee, 
  Utensils, ShoppingBag, Shirt, Gift, Plane, Bus, Train, Fuel, Wrench,
  Stethoscope, Pill, Baby, Dog, Cat, Music, Film, Book, Dumbbell, Scissors,
  Briefcase, Landmark, Receipt, Tag, MoreHorizontal, Sparkles, UserPlus
} from 'lucide-react';

// =============================================
// SUPABASE CONFIG
// =============================================
const supabaseUrl = 'https://bqbmvurwmsgitundbdxt.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJxYm12dXJ3bXNnaXR1bmRiZHh0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MjA4NzU5OTUsImV4cCI6MjAzNjQ1MTk5NX0.Mr9GtJJuCHgyXsrfCKrrVMQ7LebD3HXAgi0Rs94u5kw';

const supabase = createClient(supabaseUrl, supabaseAnonKey);

// =============================================
// UTILITY FUNCTIONS
// =============================================
const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
const formatDate = (date) => new Date(date).toLocaleDateString('pt-BR');

// Sistema de ícones disponíveis
const availableIcons = {
  Home: Home, Building: Building, Zap: Zap, Droplet: Droplet, Flame: Flame, Wifi: Wifi,
  ShoppingCart: ShoppingCart, Utensils: Utensils, Coffee: Coffee, ShoppingBag: ShoppingBag,
  Car: Car, Bus: Bus, Train: Train, Fuel: Fuel, Plane: Plane,
  Heart: Heart, Stethoscope: Stethoscope, Pill: Pill,
  GraduationCap: GraduationCap, Book: Book,
  Gamepad2: Gamepad2, Tv: Tv, Music: Music, Film: Film,
  CreditCard: CreditCard, Landmark: Landmark, Receipt: Receipt,
  Smartphone: Smartphone, Wrench: Wrench, Scissors: Scissors,
  Shirt: Shirt, Gift: Gift, Baby: Baby, Dog: Dog, Cat: Cat,
  Dumbbell: Dumbbell, Briefcase: Briefcase, Tag: Tag,
  DollarSign: DollarSign, Sparkles: Sparkles, MoreHorizontal: MoreHorizontal
};

// Categorias padrão com ícones
const defaultCategories = [
  { name: 'Moradia', icon: 'Home', color: 'blue' },
  { name: 'Alimentação', icon: 'Utensils', color: 'orange' },
  { name: 'Transporte', icon: 'Car', color: 'purple' },
  { name: 'Saúde', icon: 'Heart', color: 'red' },
  { name: 'Educação', icon: 'GraduationCap', color: 'cyan' },
  { name: 'Lazer', icon: 'Gamepad2', color: 'pink' },
  { name: 'Cartão de Crédito', icon: 'CreditCard', color: 'yellow' },
  { name: 'Serviços', icon: 'Wrench', color: 'slate' },
  { name: 'Água', icon: 'Droplet', color: 'blue' },
  { name: 'Luz', icon: 'Zap', color: 'yellow' },
  { name: 'Internet', icon: 'Wifi', color: 'cyan' },
  { name: 'Gás', icon: 'Flame', color: 'orange' },
  { name: 'Telefone', icon: 'Smartphone', color: 'green' },
  { name: 'Streaming', icon: 'Tv', color: 'purple' },
  { name: 'Supermercado', icon: 'ShoppingCart', color: 'green' },
  { name: 'Farmácia', icon: 'Pill', color: 'red' },
  { name: 'Pets', icon: 'Dog', color: 'amber' },
  { name: 'Academia', icon: 'Dumbbell', color: 'emerald' },
  { name: 'Salário', icon: 'DollarSign', color: 'green' },
  { name: 'Outros', icon: 'MoreHorizontal', color: 'slate' }
];

// Categorias de Receitas
const incomeCategories = [
  { name: 'Salário', icon: 'Briefcase', color: 'green' },
  { name: 'Freelance', icon: 'Sparkles', color: 'blue' },
  { name: 'Aluguel', icon: 'Building', color: 'purple' },
  { name: 'Investimentos', icon: 'TrendingUp', color: 'emerald' },
  { name: 'Vendas', icon: 'ShoppingBag', color: 'orange' },
  { name: 'Empréstimo', icon: 'Landmark', color: 'cyan' },
  { name: 'Presente', icon: 'Gift', color: 'pink' },
  { name: 'Reembolso', icon: 'Receipt', color: 'yellow' },
  { name: 'Comissão', icon: 'DollarSign', color: 'green' },
  { name: 'Outros', icon: 'MoreHorizontal', color: 'slate' }
];

// Função para obter o ícone de uma categoria
const getCategoryIcon = (categoryName, iconOverride = null) => {
  if (iconOverride && availableIcons[iconOverride]) {
    return availableIcons[iconOverride];
  }
  const cat = defaultCategories.find(c => c.name === categoryName);
  if (cat && availableIcons[cat.icon]) {
    return availableIcons[cat.icon];
  }
  return MoreHorizontal;
};

// Função para obter a cor de uma categoria
const getCategoryColor = (categoryName) => {
  const cat = defaultCategories.find(c => c.name === categoryName);
  return cat?.color || 'slate';
};

// Lista de nomes de categorias para compatibilidade
const categories = defaultCategories.map(c => c.name);

// =============================================
// MAIN APP COMPONENT
// =============================================
export default function FinControl() {
  // Auth State
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authMode, setAuthMode] = useState('login');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // Modo simplificado - esconde receitas e saldo negativo para usuário específico
  const SIMPLIFIED_MODE_EMAIL = 'luiz-monteiro1991@outlook.com';
  const isSimplifiedMode = session?.user?.email === SIMPLIFIED_MODE_EMAIL;

  // App State - activeTab persiste no localStorage
  const [activeTab, setActiveTabState] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('fincontrol_activeTab') || 'dashboard';
    }
    return 'dashboard';
  });
  const [wallets, setWallets] = useState([]);
  const [bills, setBills] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Flag para controlar visibilidade da página
  const [appReady, setAppReady] = useState(false);

  // Função para mudar aba e salvar no localStorage
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    if (typeof window !== 'undefined') {
      localStorage.setItem('fincontrol_activeTab', tab);
    }
  };

  // Filters - também persistem
  const [selectedMonth, setSelectedMonth] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('fincontrol_month');
      return saved ? parseInt(saved) : new Date().getMonth();
    }
    return new Date().getMonth();
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('fincontrol_year');
      return saved ? parseInt(saved) : new Date().getFullYear();
    }
    return new Date().getFullYear();
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'expense', 'income'
  const [searchTerm, setSearchTerm] = useState('');

  // Salvar mês/ano quando mudar
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('fincontrol_month', selectedMonth.toString());
      localStorage.setItem('fincontrol_year', selectedYear.toString());
    }
  }, [selectedMonth, selectedYear]);

  // Modals
  const [showBillModal, setShowBillModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [editingBillType, setEditingBillType] = useState('expense'); // 'expense' ou 'income'
  const [editingWallet, setEditingWallet] = useState(null);

  // Loading states
  const [savingBill, setSavingBill] = useState(false);
  const [savingWallet, setSavingWallet] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payingBill, setPayingBill] = useState(null);
  
  // Dashboard wallet filter
  const [dashboardWallet, setDashboardWallet] = useState('all');
  
  // Delete modal for recurring bills
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBill, setDeletingBill] = useState(null);
  const [relatedBills, setRelatedBills] = useState([]);
  const [selectedForDelete, setSelectedForDelete] = useState([]);

  const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  // =============================================
  // AUTH FUNCTIONS
  // =============================================
  // Ref para controlar se já carregou (não causa re-render)
  const dataLoadedRef = React.useRef(false);
  const initDoneRef = React.useRef(false);
  
  useEffect(() => {
    // Evitar múltiplas inicializações
    if (initDoneRef.current) return;
    initDoneRef.current = true;
    
    let isMounted = true;

    const loadUserData = async (userId) => {
      if (!isMounted || dataLoadedRef.current) return;
      
      console.log('Loading user data...');
      dataLoadedRef.current = true;
      
      try {
        const [profileResult, walletsResult, billsResult] = await Promise.all([
          supabase.from('profiles').select('*').eq('id', userId).single(),
          supabase.from('wallets').select('*').eq('user_id', userId),
          supabase.from('bills').select('*').eq('user_id', userId).gte('due_date', new Date(new Date().setMonth(new Date().getMonth() - 2)).toISOString().split('T')[0])
        ]);
        
        if (!isMounted) return;
        
        if (profileResult.data) setProfile(profileResult.data);
        if (walletsResult.data) setWallets(walletsResult.data);
        if (billsResult.data) setBills(billsResult.data.sort((a, b) => new Date(a.due_date) - new Date(b.due_date)));
        
        console.log('Data loaded successfully');
      } catch (error) {
        console.error('Error loading data:', error);
        dataLoadedRef.current = false;
      }
    };

    const init = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        setSession(currentSession);
        
        if (currentSession) {
          await loadUserData(currentSession.user.id);
        }
      } catch (error) {
        console.error('Init error:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setAppReady(true);
        }
      }
    };

    init();

    // Listener SIMPLES - só para logout e login novo
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!isMounted) return;
      
      console.log('Auth event:', event);
      
      if (event === 'SIGNED_OUT') {
        setSession(null);
        setProfile(null);
        setWallets([]);
        setBills([]);
        dataLoadedRef.current = false;
        setIsLoading(false);
      } else if (event === 'SIGNED_IN' && newSession && !dataLoadedRef.current) {
        setSession(newSession);
        loadUserData(newSession.user.id).finally(() => {
          if (isMounted) setIsLoading(false);
        });
      }
      // Ignorar TOKEN_REFRESHED, INITIAL_SESSION, USER_UPDATED, etc
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const loadProfile = async (userId) => {
    console.log('Loading profile for user:', userId);
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (error) console.error('Error loading profile:', error);
    if (data) {
      console.log('Profile loaded:', data.name);
      setProfile(data);
    }
  };

  const loadData = async (userId) => {
    console.log('Loading data for user:', userId);
    const startTime = Date.now();
    
    // Calcular período: 2 meses atrás até 12 meses à frente
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    const endDate = new Date(today.getFullYear(), today.getMonth() + 13, 0);
    const startDateStr = startDate.toISOString().split('T')[0];
    const endDateStr = endDate.toISOString().split('T')[0];
    
    // Carregar em PARALELO (muito mais rápido!)
    const [walletsResult, billsResult] = await Promise.all([
      // Load wallets
      supabase
        .from('wallets')
        .select('id, name, description, color, balance, user_id, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false }),
      
      // Load bills - APENAS período relevante
      supabase
        .from('bills')
        .select('*')
        .eq('user_id', userId)
        .gte('due_date', startDateStr)
        .lte('due_date', endDateStr)
        .order('due_date', { ascending: true })
    ]);
    
    const { data: walletsData, error: walletsError } = walletsResult;
    const { data: billsData, error: billsError } = billsResult;
    
    if (walletsError) console.error('Error loading wallets:', walletsError);
    if (billsError) console.error('Error loading bills:', billsError);
    
    const loadTime = Date.now() - startTime;
    console.log(`Data loaded in ${loadTime}ms - Wallets: ${walletsData?.length || 0}, Bills: ${billsData?.length || 0}`);
    
    if (walletsData) setWallets(walletsData);
    if (billsData) setBills(billsData);
  };

  const handleLogin = async (email, password) => {
    setAuthLoading(true);
    setAuthError('');

    const { error } = await supabase.auth.signInWithPassword({ email, password });
    
    if (error) {
      setAuthError(error.message === 'Invalid login credentials' ? 'Email ou senha incorretos' : error.message);
    }
    setAuthLoading(false);
  };

  const handleRegister = async (name, email, password) => {
    setAuthLoading(true);
    setAuthError('');

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name }
      }
    });

    if (error) {
      setAuthError(error.message);
    } else {
      setAuthError('');
      setAuthMode('login');
      alert('Conta criada! Verifique seu email se necessário.');
    }
    setAuthLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setActiveTab('dashboard');
  };

  // =============================================
  // CRUD FUNCTIONS
  // =============================================
  const saveBill = async (billData) => {
    setSavingBill(true);
    const userId = session.user.id;

    // Se for recorrente, criar múltiplas contas
    if (billData.is_recurring && !editingBill) {
      const billsToCreate = [];
      const startDate = new Date(billData.due_date + 'T00:00:00');
      
      for (let i = 0; i < billData.recurrence_count; i++) {
        const newDate = new Date(startDate);
        
        switch(billData.recurrence_type) {
          case 'weekly':
            newDate.setDate(startDate.getDate() + (i * 7));
            break;
          case 'monthly':
            newDate.setMonth(startDate.getMonth() + i);
            break;
          case 'quarterly':
            newDate.setMonth(startDate.getMonth() + (i * 3));
            break;
          case 'yearly':
            newDate.setFullYear(startDate.getFullYear() + i);
            break;
          default:
            break;
        }

        billsToCreate.push({
          user_id: userId,
          wallet_id: billData.wallet_id || null,
          name: billData.name,
          amount: parseFloat(billData.amount),
          due_date: newDate.toISOString().split('T')[0],
          category: billData.category,
          icon: billData.icon || null,
          status: 'pending',
          is_recurring: true,
          recurrence_type: billData.recurrence_type,
          notes: billData.notes || null,
          pix_code: billData.pix_code || null,
          payer: billData.payer || null,
          type: billData.type || 'expense',
          notification_enabled: billData.notification_enabled || false,
          notification_days_before: billData.notification_days_before || 3,
          attachment_url: billData.attachment_url || null,
          attachment_name: billData.attachment_name || null
        });
      }

      const { data, error } = await supabase
        .from('bills')
        .insert(billsToCreate)
        .select();

      if (data) {
        setBills([...bills, ...data]);
      }
      if (error) console.error('Erro ao criar contas recorrentes:', error);
    } else {
      // Conta única ou edição
      const billRecord = {
        user_id: userId,
        wallet_id: billData.wallet_id || null,
        parent_id: billData.parent_id || null,
        name: billData.name,
        amount: parseFloat(billData.amount),
        due_date: billData.due_date,
        category: billData.category,
        icon: billData.icon || null,
        status: billData.status || 'pending',
        paid_date: billData.paid_date || null,
        is_recurring: billData.is_recurring || false,
        recurrence_type: billData.recurrence_type || null,
        notes: billData.notes || null,
        pix_code: billData.pix_code || null,
        payer: billData.payer || null,
        type: billData.type || 'expense',
        notification_enabled: billData.notification_enabled || false,
        notification_days_before: billData.notification_days_before || 3,
        attachment_url: billData.attachment_url || null,
        attachment_name: billData.attachment_name || null,
        receipt_url: billData.receipt_url || null,
        receipt_name: billData.receipt_name || null
      };

      if (editingBill) {
        const { data, error } = await supabase
          .from('bills')
          .update(billRecord)
          .eq('id', editingBill.id)
          .select()
          .single();

        if (data) {
          setBills(bills.map(b => b.id === editingBill.id ? data : b));
        }
        if (error) console.error('Erro ao atualizar:', error);
      } else {
        const { data, error } = await supabase
          .from('bills')
          .insert(billRecord)
          .select()
          .single();

        if (data) {
          setBills([...bills, data]);
        }
        if (error) console.error('Erro ao criar:', error);
      }
    }

    setSavingBill(false);
    setShowBillModal(false);
    setEditingBill(null);
  };

  const deleteBill = async (billId, skipModal = false) => {
    const bill = bills.find(b => b.id === billId);
    if (!bill) return;
    
    // Se é recorrente, verificar outras parcelas
    if (bill.is_recurring && !skipModal) {
      const related = bills.filter(b => 
        b.name === bill.name && 
        b.is_recurring && 
        b.id !== billId &&
        b.wallet_id === bill.wallet_id
      );
      
      if (related.length > 0) {
        setDeletingBill(bill);
        setRelatedBills(related);
        setSelectedForDelete([billId]);
        setShowDeleteModal(true);
        return;
      }
    }
    
    if (!skipModal && !window.confirm('Deseja excluir esta conta?')) return;

    const { error } = await supabase
      .from('bills')
      .delete()
      .eq('id', billId);

    if (!error) {
      setBills(bills.filter(b => b.id !== billId));
    }
  };

  const deleteMultipleBills = async (billIds) => {
    const { error } = await supabase
      .from('bills')
      .delete()
      .in('id', billIds);

    if (!error) {
      setBills(bills.filter(b => !billIds.includes(b.id)));
    }
    setShowDeleteModal(false);
    setDeletingBill(null);
    setRelatedBills([]);
    setSelectedForDelete([]);
  };

  const deleteRecurringGroup = async (groupName, walletId, deleteOption) => {
    // deleteOption: 'all' = todas, 'pending' = só pendentes, 'paid' = só pagas
    const toDelete = bills.filter(b => 
      b.name === groupName && 
      b.is_recurring && 
      b.wallet_id === walletId &&
      (deleteOption === 'all' || 
       (deleteOption === 'pending' && b.status === 'pending') ||
       (deleteOption === 'paid' && b.status === 'paid'))
    );
    
    if (toDelete.length === 0) return;
    
    const ids = toDelete.map(b => b.id);
    await deleteMultipleBills(ids);
  };

  const toggleBillStatus = async (bill) => {
    const newStatus = bill.status === 'paid' ? 'pending' : 'paid';
    const paidDate = newStatus === 'paid' ? new Date().toISOString().split('T')[0] : null;
    const isIncome = bill.type === 'income';

    const { data } = await supabase
      .from('bills')
      .update({ status: newStatus, paid_date: paidDate, scheduled: false })
      .eq('id', bill.id)
      .select()
      .single();

    if (data) {
      setBills(bills.map(b => b.id === bill.id ? data : b));
      
      // Ajustar saldo da carteira
      if (bill.wallet_id) {
        const wallet = wallets.find(w => w.id === bill.wallet_id);
        if (wallet) {
          let adjustment;
          if (isIncome) {
            // RECEITA: ao receber SOMA, ao desmarcar DESCONTA
            adjustment = newStatus === 'paid' ? parseFloat(bill.amount) : -parseFloat(bill.amount);
          } else {
            // DESPESA: ao pagar DESCONTA, ao desmarcar DEVOLVE
            adjustment = newStatus === 'paid' ? -parseFloat(bill.amount) : parseFloat(bill.amount);
          }
          const newBalance = (wallet.balance || 0) + adjustment;
          
          await supabase
            .from('wallets')
            .update({ balance: newBalance })
            .eq('id', wallet.id);
          
          setWallets(wallets.map(w => w.id === wallet.id ? { ...w, balance: newBalance } : w));
        }
      }
    }
  };

  const toggleScheduled = async (bill) => {
    const newScheduled = !bill.scheduled;

    const { data } = await supabase
      .from('bills')
      .update({ scheduled: newScheduled })
      .eq('id', bill.id)
      .select()
      .single();

    if (data) {
      setBills(bills.map(b => b.id === bill.id ? data : b));
    }
  };

  const openPaymentModal = (bill) => {
    setPayingBill(bill);
    setShowPaymentModal(true);
  };

  const markPaidWithReceipt = async (billId, receiptUrl, receiptName) => {
    // Encontrar a conta
    const bill = bills.find(b => b.id === billId);
    const isIncome = bill?.type === 'income';
    
    const { data } = await supabase
      .from('bills')
      .update({ 
        status: 'paid', 
        paid_date: new Date().toISOString().split('T')[0],
        receipt_url: receiptUrl || null,
        receipt_name: receiptName || null
      })
      .eq('id', billId)
      .select()
      .single();

    if (data) {
      setBills(bills.map(b => b.id === billId ? data : b));
      
      // Ajustar saldo da carteira
      if (bill && bill.wallet_id) {
        const wallet = wallets.find(w => w.id === bill.wallet_id);
        if (wallet) {
          // RECEITA soma, DESPESA desconta
          const adjustment = isIncome ? parseFloat(bill.amount) : -parseFloat(bill.amount);
          const newBalance = (wallet.balance || 0) + adjustment;
          await supabase
            .from('wallets')
            .update({ balance: newBalance })
            .eq('id', wallet.id);
          
          setWallets(wallets.map(w => w.id === wallet.id ? { ...w, balance: newBalance } : w));
        }
      }
    }
    setShowPaymentModal(false);
    setPayingBill(null);
  };

  const saveWallet = async (walletData) => {
    setSavingWallet(true);
    const userId = session.user.id;

    const walletRecord = {
      user_id: userId,
      name: walletData.name,
      description: walletData.description || null,
      color: walletData.color || '#10b981'
    };

    if (editingWallet) {
      const { data } = await supabase
        .from('wallets')
        .update(walletRecord)
        .eq('id', editingWallet.id)
        .select()
        .single();

      if (data) {
        setWallets(wallets.map(w => w.id === editingWallet.id ? data : w));
      }
    } else {
      const { data } = await supabase
        .from('wallets')
        .insert(walletRecord)
        .select()
        .single();

      if (data) {
        setWallets([...wallets, data]);
      }
    }

    setSavingWallet(false);
    setShowWalletModal(false);
    setEditingWallet(null);
  };

  const deleteWallet = async (walletId) => {
    if (!window.confirm('Deseja excluir esta carteira?')) return;

    const { error } = await supabase
      .from('wallets')
      .delete()
      .eq('id', walletId);

    if (!error) {
      setWallets(wallets.filter(w => w.id !== walletId));
    }
  };

  const updateWalletBalance = async (walletId, newBalance) => {
    const { data, error } = await supabase
      .from('wallets')
      .update({ balance: newBalance })
      .eq('id', walletId)
      .select()
      .single();

    if (data) {
      setWallets(wallets.map(w => w.id === walletId ? { ...w, balance: newBalance } : w));
    }
    if (error) {
      alert('Erro ao atualizar saldo: ' + error.message);
    }
  };

  const updateProfile = async (profileData) => {
    const { data, error } = await supabase
      .from('profiles')
      .update(profileData)
      .eq('id', session.user.id)
      .select()
      .single();

    if (data) {
      setProfile(data);
      alert('Perfil atualizado!');
    }
    if (error) {
      alert('Erro ao atualizar: ' + error.message);
    }
  };

  const uploadAttachment = async (file) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${session.user.id}/${Date.now()}.${fileExt}`;

    const { error } = await supabase.storage
      .from('attachments')
      .upload(fileName, file);

    if (error) {
      console.error('Erro upload:', error);
      return null;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('attachments')
      .getPublicUrl(fileName);

    return { url: publicUrl, name: file.name };
  };

  // =============================================
  // COMPUTED DATA
  // =============================================
  const filteredBills = bills.filter(bill => {
    const billDate = new Date(bill.due_date + 'T00:00:00');
    const monthMatch = billDate.getMonth() === selectedMonth && billDate.getFullYear() === selectedYear;
    const statusMatch = statusFilter === 'all' || bill.status === statusFilter;
    const typeMatch = typeFilter === 'all' || (bill.type || 'expense') === typeFilter;
    const searchMatch = bill.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       bill.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (bill.payer && bill.payer.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return monthMatch && statusMatch && typeMatch && searchMatch;
  });

  // Separar despesas e receitas para stats
  const expenses = filteredBills.filter(b => (b.type || 'expense') === 'expense');
  const incomes = filteredBills.filter(b => b.type === 'income');

  const stats = {
    // Despesas
    totalExpenses: expenses.reduce((acc, b) => acc + parseFloat(b.amount), 0),
    paidExpenses: expenses.filter(b => b.status === 'paid').reduce((acc, b) => acc + parseFloat(b.amount), 0),
    pendingExpenses: expenses.filter(b => b.status === 'pending').reduce((acc, b) => acc + parseFloat(b.amount), 0),
    overdueExpenses: expenses.filter(b => b.status === 'pending' && new Date(b.due_date + 'T00:00:00') < new Date()).reduce((acc, b) => acc + parseFloat(b.amount), 0),
    expenseCount: expenses.length,
    paidExpenseCount: expenses.filter(b => b.status === 'paid').length,
    pendingExpenseCount: expenses.filter(b => b.status === 'pending').length,
    overdueExpenseCount: expenses.filter(b => b.status === 'pending' && new Date(b.due_date + 'T00:00:00') < new Date()).length,
    
    // Receitas
    totalIncome: incomes.reduce((acc, b) => acc + parseFloat(b.amount), 0),
    receivedIncome: incomes.filter(b => b.status === 'paid').reduce((acc, b) => acc + parseFloat(b.amount), 0),
    pendingIncome: incomes.filter(b => b.status === 'pending').reduce((acc, b) => acc + parseFloat(b.amount), 0),
    overdueIncome: incomes.filter(b => b.status === 'pending' && new Date(b.due_date + 'T00:00:00') < new Date()).reduce((acc, b) => acc + parseFloat(b.amount), 0),
    incomeCount: incomes.length,
    receivedIncomeCount: incomes.filter(b => b.status === 'paid').length,
    pendingIncomeCount: incomes.filter(b => b.status === 'pending').length,
    overdueIncomeCount: incomes.filter(b => b.status === 'pending' && new Date(b.due_date + 'T00:00:00') < new Date()).length,
    
    // Balanço
    balance: incomes.filter(b => b.status === 'paid').reduce((acc, b) => acc + parseFloat(b.amount), 0) - 
             expenses.filter(b => b.status === 'paid').reduce((acc, b) => acc + parseFloat(b.amount), 0),
    
    // Totais gerais (para compatibilidade)
    total: filteredBills.reduce((acc, b) => acc + parseFloat(b.amount), 0),
    paid: filteredBills.filter(b => b.status === 'paid').reduce((acc, b) => acc + parseFloat(b.amount), 0),
    pending: filteredBills.filter(b => b.status === 'pending').reduce((acc, b) => acc + parseFloat(b.amount), 0),
    overdue: filteredBills.filter(b => b.status === 'pending' && new Date(b.due_date + 'T00:00:00') < new Date()).reduce((acc, b) => acc + parseFloat(b.amount), 0),
    count: filteredBills.length,
    paidCount: filteredBills.filter(b => b.status === 'paid').length,
    pendingCount: filteredBills.filter(b => b.status === 'pending').length,
    overdueCount: filteredBills.filter(b => b.status === 'pending' && new Date(b.due_date + 'T00:00:00') < new Date()).length
  };

  const categoryBreakdown = categories.map(cat => ({
    name: cat,
    total: filteredBills.filter(b => b.category === cat).reduce((acc, b) => acc + parseFloat(b.amount), 0),
    count: filteredBills.filter(b => b.category === cat).length
  })).filter(c => c.count > 0).sort((a, b) => b.total - a.total);

  // Breakdown por Carteira
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const walletBreakdown = wallets.map(wallet => {
    const walletBills = filteredBills.filter(b => b.wallet_id === wallet.id);
    const pendingBills = walletBills.filter(b => b.status === 'pending');
    const overdueBills = pendingBills.filter(b => new Date(b.due_date + 'T00:00:00') < today);
    const pendingNotOverdue = pendingBills.filter(b => new Date(b.due_date + 'T00:00:00') >= today);
    const pendingTotal = pendingBills.reduce((acc, b) => acc + parseFloat(b.amount), 0);
    
    return {
      id: wallet.id,
      name: wallet.name,
      balance: wallet.balance || 0,
      total: walletBills.reduce((acc, b) => acc + parseFloat(b.amount), 0),
      paid: walletBills.filter(b => b.status === 'paid').reduce((acc, b) => acc + parseFloat(b.amount), 0),
      pending: pendingNotOverdue.reduce((acc, b) => acc + parseFloat(b.amount), 0),
      overdue: overdueBills.reduce((acc, b) => acc + parseFloat(b.amount), 0),
      count: walletBills.length,
      pendingCount: pendingNotOverdue.length,
      overdueCount: overdueBills.length,
      remaining: (wallet.balance || 0) - pendingTotal
    };
  }).filter(w => w.count > 0);

  // Contas sem carteira
  const noWalletBillsFiltered = filteredBills.filter(b => !b.wallet_id);
  const noWalletPending = noWalletBillsFiltered.filter(b => b.status === 'pending');
  const noWalletOverdue = noWalletPending.filter(b => new Date(b.due_date + 'T00:00:00') < today);
  const noWalletPendingNotOverdue = noWalletPending.filter(b => new Date(b.due_date + 'T00:00:00') >= today);
  
  const noWalletBills = {
    name: 'Sem Carteira',
    total: noWalletBillsFiltered.reduce((acc, b) => acc + parseFloat(b.amount), 0),
    paid: noWalletBillsFiltered.filter(b => b.status === 'paid').reduce((acc, b) => acc + parseFloat(b.amount), 0),
    pending: noWalletPendingNotOverdue.reduce((acc, b) => acc + parseFloat(b.amount), 0),
    overdue: noWalletOverdue.reduce((acc, b) => acc + parseFloat(b.amount), 0),
    count: noWalletBillsFiltered.length,
    pendingCount: noWalletPendingNotOverdue.length,
    overdueCount: noWalletOverdue.length
  };

  const upcomingBills = bills
    .filter(b => b.status === 'pending')
    .filter(b => {
      const dueDate = new Date(b.due_date + 'T00:00:00');
      const today = new Date();
      const weekLater = new Date();
      weekLater.setDate(today.getDate() + 7);
      return dueDate >= today && dueDate <= weekLater;
    })
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date + 'T00:00:00'));

  // Carregar dados de meses adicionais se necessário
  const loadMonthData = async (year, month) => {
    if (!session) return;
    
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    const startDateStr = startDate.toISOString().split('T')[0];
    const endDateStr = endDate.toISOString().split('T')[0];
    
    // Verificar se já temos dados desse mês
    const hasData = bills.some(b => {
      const billDate = new Date(b.due_date);
      return billDate.getFullYear() === year && billDate.getMonth() === month;
    });
    
    if (!hasData) {
      console.log(`Loading additional data for ${month + 1}/${year}`);
      const { data: newBills } = await supabase
        .from('bills')
        .select('*')
        .eq('user_id', session.user.id)
        .gte('due_date', startDateStr)
        .lte('due_date', endDateStr)
        .order('due_date', { ascending: true });
      
      if (newBills && newBills.length > 0) {
        // Merge sem duplicatas
        setBills(prev => {
          const existingIds = new Set(prev.map(b => b.id));
          const uniqueNew = newBills.filter(b => !existingIds.has(b.id));
          return [...prev, ...uniqueNew].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
        });
      }
    }
  };

  const navigateMonth = (direction) => {
    let newMonth = selectedMonth;
    let newYear = selectedYear;
    
    if (direction === 'prev') {
      if (selectedMonth === 0) {
        newMonth = 11;
        newYear = selectedYear - 1;
      } else {
        newMonth = selectedMonth - 1;
      }
    } else {
      if (selectedMonth === 11) {
        newMonth = 0;
        newYear = selectedYear + 1;
      } else {
        newMonth = selectedMonth + 1;
      }
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
    
    // Carregar dados do mês se necessário
    loadMonthData(newYear, newMonth);
  };

  // =============================================
  // LOADING SCREEN
  // =============================================
  // Timeout de segurança - se demorar mais de 10s, mostra botão de reload
  const [loadingTooLong, setLoadingTooLong] = useState(false);
  
  useEffect(() => {
    if (isLoading && !appReady) {
      const timeout = setTimeout(() => setLoadingTooLong(true), 10000);
      return () => clearTimeout(timeout);
    } else {
      setLoadingTooLong(false);
    }
  }, [isLoading, appReady]);
  
  // Só mostrar loading se ainda não carregou o app pela primeira vez
  if (isLoading && !appReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-emerald-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400 mb-4">Carregando...</p>
          {loadingTooLong && (
            <button 
              onClick={() => window.location.reload()}
              className="text-sm text-emerald-400 hover:text-emerald-300 underline"
            >
              Está demorando? Clique para recarregar
            </button>
          )}
        </div>
      </div>
    );
  }

  // =============================================
  // AUTH SCREEN
  // =============================================
  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 flex items-center justify-center p-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative w-full max-w-md">
          <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl mb-4 shadow-lg shadow-emerald-500/20">
                <Wallet className="w-8 h-8 text-slate-900" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">FinControl</h1>
              <p className="text-slate-400 text-sm">Organize suas finanças de forma simples</p>
            </div>

            {authError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {authError}
              </div>
            )}

            {authMode === 'login' ? (
              <LoginForm
                onSubmit={handleLogin}
                onSwitch={() => { setAuthMode('register'); setAuthError(''); }}
                loading={authLoading}
              />
            ) : (
              <RegisterForm
                onSubmit={handleRegister}
                onSwitch={() => { setAuthMode('login'); setAuthError(''); }}
                loading={authLoading}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // =============================================
  // MAIN APP
  // =============================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative flex min-h-screen">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:flex flex-col w-64 bg-slate-900/50 backdrop-blur-xl border-r border-slate-800/50 p-4">
          <div className="flex items-center gap-3 mb-8 px-2">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Wallet className="w-5 h-5 text-slate-900" />
            </div>
            <span className="font-bold text-white text-lg">FinControl</span>
          </div>

          <nav className="flex-1 space-y-1">
            {[
              { id: 'dashboard', icon: Home, label: 'Dashboard' },
              { id: 'bills', icon: FileText, label: 'Contas' },
              { id: 'wallets', icon: CreditCard, label: 'Carteiras' },
              { id: 'recurring', icon: Repeat, label: 'Recorrentes' },
              { id: 'notifications', icon: Bell, label: 'Notificações' },
              { id: 'profile', icon: User, label: 'Perfil' },
            ].map(item => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === item.id
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>

          <div className="pt-4 border-t border-slate-800/50 mt-4">
            <div className="px-4 py-2 mb-2">
              <p className="text-white font-medium text-sm truncate">{profile?.name}</p>
              <p className="text-slate-500 text-xs truncate">{session.user.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Sair</span>
            </button>
          </div>
        </aside>

        {/* Mobile Header */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-xl border-b border-slate-800/50">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-lg flex items-center justify-center">
                <Wallet className="w-4 h-4 text-slate-900" />
              </div>
              <span className="font-bold text-white">FinControl</span>
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-400 hover:text-white"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="absolute top-full left-0 right-0 bg-slate-900/95 backdrop-blur-xl border-b border-slate-800/50 p-4 space-y-1">
              {[
                { id: 'dashboard', icon: Home, label: 'Dashboard' },
                { id: 'bills', icon: FileText, label: 'Contas' },
                { id: 'wallets', icon: CreditCard, label: 'Carteiras' },
                { id: 'recurring', icon: Repeat, label: 'Recorrentes' },
                { id: 'notifications', icon: Bell, label: 'Notificações' },
                { id: 'profile', icon: User, label: 'Perfil' },
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    activeTab === item.id
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'text-slate-400 hover:bg-slate-800/50'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </button>
              ))}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-xl"
              >
                <LogOut className="w-5 h-5" />
                <span>Sair</span>
              </button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 pt-20 lg:pt-8 overflow-auto">
          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-white">Olá, {profile?.name || 'Usuário'}!</h1>
                  <p className="text-slate-400">Aqui está o resumo das suas contas</p>
                </div>
                
                <div className="flex items-center gap-2 bg-slate-800/50 rounded-xl p-1">
                  <button onClick={() => navigateMonth('prev')} className="p-2 text-slate-400 hover:text-white transition-colors">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-white font-medium px-4 min-w-[140px] text-center">
                    {monthNames[selectedMonth]} {selectedYear}
                  </span>
                  <button onClick={() => navigateMonth('next')} className="p-2 text-slate-400 hover:text-white transition-colors">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Wallet Selector - NO TOPO */}
              <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/30 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Wallet className="w-5 h-5 text-emerald-400" />
                  <span className="text-white font-semibold">Selecione a Carteira</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setDashboardWallet('all')}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      dashboardWallet === 'all'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    Todas as Carteiras
                  </button>
                  {wallets.map(wallet => {
                    const walletPending = filteredBills.filter(b => b.wallet_id === wallet.id && b.status === 'pending');
                    const pendingTotal = walletPending.reduce((acc, b) => acc + parseFloat(b.amount), 0);
                    return (
                      <button
                        key={wallet.id}
                        onClick={() => setDashboardWallet(wallet.id)}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                          dashboardWallet === wallet.id
                            ? 'bg-emerald-500 text-white'
                            : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        <CreditCard className="w-4 h-4" />
                        {wallet.name}
                        {walletPending.length > 0 && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${dashboardWallet === wallet.id ? 'bg-white/20' : 'bg-yellow-500/20 text-yellow-400'}`}>
                            {walletPending.length}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                
                {/* Info da carteira selecionada */}
                {dashboardWallet !== 'all' && (() => {
                  const wallet = wallets.find(w => w.id === dashboardWallet);
                  if (!wallet) return null;
                  const walletBillsFiltered = filteredBills.filter(b => b.wallet_id === wallet.id && (b.type || 'expense') === 'expense');
                  const pendingTotal = walletBillsFiltered.filter(b => b.status === 'pending').reduce((acc, b) => acc + parseFloat(b.amount), 0);
                  // No modo simplificado, nunca mostra valor negativo
                  const remaining = isSimplifiedMode 
                    ? Math.max(0, (wallet.balance || 0) - pendingTotal)
                    : (wallet.balance || 0) - pendingTotal;
                  
                  return (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3">
                        <p className="text-slate-400 text-xs">Saldo</p>
                        <p className="text-emerald-400 font-bold text-lg">{formatCurrency(wallet.balance || 0)}</p>
                      </div>
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">
                        <p className="text-slate-400 text-xs">Pendente</p>
                        <p className="text-yellow-400 font-bold text-lg">{formatCurrency(pendingTotal)}</p>
                      </div>
                      {/* No modo simplificado, esconde o card "Falta" quando negativo */}
                      {(remaining >= 0 || !isSimplifiedMode) && (
                        <div className={`${remaining >= 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'} border rounded-xl p-3`}>
                          <p className="text-slate-400 text-xs">{remaining >= 0 ? 'Sobra' : 'Falta'}</p>
                          <p className={`font-bold text-lg ${remaining >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrency(Math.abs(remaining))}</p>
                        </div>
                      )}
                      <button
                        onClick={() => setActiveTab('wallets')}
                        className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-3 hover:bg-slate-700 transition-all text-left"
                      >
                        <p className="text-slate-400 text-xs">Gerenciar</p>
                        <p className="text-white font-medium">+ Adicionar Saldo</p>
                      </button>
                    </div>
                  );
                })()}
              </div>

              {/* Stats Cards - Filtrados pela carteira */}
              {(() => {
                const dashboardBills = dashboardWallet === 'all' 
                  ? filteredBills 
                  : filteredBills.filter(b => b.wallet_id === dashboardWallet);
                
                const dashboardExpenses = dashboardBills.filter(b => (b.type || 'expense') === 'expense');
                const dashboardIncomes = dashboardBills.filter(b => b.type === 'income');
                
                // Verifica se tem alguma receita cadastrada (em todo o sistema, não só no mês)
                const hasAnyIncome = bills.some(b => b.type === 'income');
                
                // Modo simplificado: esconde receitas se não tiver nenhuma E for o usuário específico
                const hideIncomeCards = isSimplifiedMode && !hasAnyIncome;
                
                const dashboardStats = {
                  // Despesas
                  totalExpenses: dashboardExpenses.reduce((acc, b) => acc + parseFloat(b.amount), 0),
                  paidExpenses: dashboardExpenses.filter(b => b.status === 'paid').reduce((acc, b) => acc + parseFloat(b.amount), 0),
                  pendingExpenses: dashboardExpenses.filter(b => b.status === 'pending').reduce((acc, b) => acc + parseFloat(b.amount), 0),
                  overdueExpenses: dashboardExpenses.filter(b => b.status === 'pending' && new Date(b.due_date + 'T00:00:00') < new Date()).reduce((acc, b) => acc + parseFloat(b.amount), 0),
                  expenseCount: dashboardExpenses.length,
                  pendingExpenseCount: dashboardExpenses.filter(b => b.status === 'pending').length,
                  overdueExpenseCount: dashboardExpenses.filter(b => b.status === 'pending' && new Date(b.due_date + 'T00:00:00') < new Date()).length,
                  
                  // Receitas
                  totalIncome: dashboardIncomes.reduce((acc, b) => acc + parseFloat(b.amount), 0),
                  receivedIncome: dashboardIncomes.filter(b => b.status === 'paid').reduce((acc, b) => acc + parseFloat(b.amount), 0),
                  pendingIncome: dashboardIncomes.filter(b => b.status === 'pending').reduce((acc, b) => acc + parseFloat(b.amount), 0),
                  incomeCount: dashboardIncomes.length,
                  pendingIncomeCount: dashboardIncomes.filter(b => b.status === 'pending').length,
                  
                  // Balanço - no modo simplificado, nunca mostra negativo
                  balance: isSimplifiedMode 
                    ? Math.max(0, dashboardIncomes.filter(b => b.status === 'paid').reduce((acc, b) => acc + parseFloat(b.amount), 0) - dashboardExpenses.filter(b => b.status === 'paid').reduce((acc, b) => acc + parseFloat(b.amount), 0))
                    : dashboardIncomes.filter(b => b.status === 'paid').reduce((acc, b) => acc + parseFloat(b.amount), 0) - dashboardExpenses.filter(b => b.status === 'paid').reduce((acc, b) => acc + parseFloat(b.amount), 0)
                };
                
                // Modo simplificado sem receitas cadastradas - exibe apenas despesas
                if (hideIncomeCards) {
                  return (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard icon={DollarSign} label="Total do Mês" value={formatCurrency(dashboardStats.totalExpenses)} subtext={`${dashboardStats.expenseCount} contas`} color="emerald" />
                      <StatCard icon={CheckCircle} label="Pagas" value={formatCurrency(dashboardStats.paidExpenses)} subtext={`${dashboardExpenses.filter(b => b.status === 'paid').length} contas`} color="green" />
                      <StatCard icon={Clock} label="Pendentes" value={formatCurrency(dashboardStats.pendingExpenses)} subtext={`${dashboardStats.pendingExpenseCount} pendentes`} color="yellow" />
                      <StatCard icon={AlertCircle} label="Vencidas" value={formatCurrency(dashboardStats.overdueExpenses)} subtext={`${dashboardStats.overdueExpenseCount} contas`} color="red" />
                    </div>
                  );
                }
                
                return (
                  <>
                    {/* Cards de Despesas e Receitas */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard icon={TrendingDown} label="Despesas" value={formatCurrency(dashboardStats.totalExpenses)} subtext={`${dashboardStats.expenseCount} contas`} color="red" />
                      <StatCard icon={TrendingUp} label="Receitas" value={formatCurrency(dashboardStats.totalIncome)} subtext={`${dashboardStats.incomeCount} receitas`} color="green" />
                      <StatCard icon={Clock} label="A Pagar" value={formatCurrency(dashboardStats.pendingExpenses)} subtext={`${dashboardStats.pendingExpenseCount} pendentes`} color="yellow" />
                      <StatCard icon={DollarSign} label="A Receber" value={formatCurrency(dashboardStats.pendingIncome)} subtext={`${dashboardStats.pendingIncomeCount} pendentes`} color="blue" />
                    </div>
                    
                    {/* Card de Balanço - esconde se modo simplificado e negativo */}
                    {!(isSimplifiedMode && dashboardStats.balance <= 0) && (
                      <div className={`p-4 rounded-2xl border ${dashboardStats.balance >= 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dashboardStats.balance >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                              {dashboardStats.balance >= 0 ? <TrendingUp className="w-5 h-5 text-green-400" /> : <TrendingDown className="w-5 h-5 text-red-400" />}
                            </div>
                            <div>
                              <p className="text-slate-400 text-sm">Balanço do Mês</p>
                              <p className={`text-2xl font-bold ${dashboardStats.balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {dashboardStats.balance >= 0 ? '+' : ''}{formatCurrency(dashboardStats.balance)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right text-sm">
                            <p className="text-green-400">+{formatCurrency(dashboardStats.receivedIncome)} recebido</p>
                            <p className="text-red-400">-{formatCurrency(dashboardStats.paidExpenses)} pago</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Próximos Vencimentos - Filtrados pela carteira */}
              {(() => {
                const dashboardUpcoming = dashboardWallet === 'all'
                  ? upcomingBills
                  : upcomingBills.filter(b => b.wallet_id === dashboardWallet);
                
                return (
                  <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/30 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                        <Bell className="w-5 h-5 text-emerald-400" />
                        Próximos Vencimentos
                      </h2>
                      <span className="text-xs text-slate-500">Próximos 7 dias</span>
                    </div>
                    
                    {dashboardUpcoming.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">Nenhuma conta vencendo em breve</p>
                    ) : (
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {dashboardUpcoming.slice(0, 6).map(bill => {
                          const daysUntil = Math.ceil((new Date(bill.due_date + 'T00:00:00') - new Date()) / (1000 * 60 * 60 * 24));
                          const wallet = wallets.find(w => w.id === bill.wallet_id);
                          return (
                            <div key={bill.id} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-xl">
                              <div className="flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full ${daysUntil <= 1 ? 'bg-red-400' : daysUntil <= 3 ? 'bg-yellow-400' : 'bg-emerald-400'}`}></div>
                                <div>
                                  <p className="text-white font-medium text-sm">{bill.name}</p>
                                  <p className="text-slate-500 text-xs">
                                    {formatDate(bill.due_date)} • {daysUntil === 0 ? 'Hoje' : daysUntil === 1 ? 'Amanhã' : `${daysUntil} dias`}
                                    {wallet && <span className="ml-1 text-slate-600">• {wallet.name}</span>}
                                  </p>
                                </div>
                              </div>
                              <span className="text-white font-semibold">{formatCurrency(bill.amount)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Por Categoria */}
              <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/30 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-emerald-400" />
                    Por Categoria
                  </h2>
                </div>
                
                {(() => {
                  const dashboardBills = dashboardWallet === 'all' 
                    ? filteredBills 
                    : filteredBills.filter(b => b.wallet_id === dashboardWallet);
                  const catBreakdown = categories.map(cat => ({
                    name: cat,
                    total: dashboardBills.filter(b => b.category === cat).reduce((acc, b) => acc + parseFloat(b.amount), 0),
                    count: dashboardBills.filter(b => b.category === cat).length
                  })).filter(c => c.count > 0).sort((a, b) => b.total - a.total);
                  const totalAmount = dashboardBills.reduce((acc, b) => acc + parseFloat(b.amount), 0);
                  
                  return catBreakdown.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">Nenhuma conta neste período</p>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {catBreakdown.slice(0, 6).map((cat, idx) => {
                        const percentage = totalAmount > 0 ? (cat.total / totalAmount * 100) : 0;
                        const colors = ['bg-emerald-500', 'bg-teal-500', 'bg-cyan-500', 'bg-blue-500', 'bg-purple-500', 'bg-pink-500'];
                        return (
                          <div key={cat.name} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-slate-300">{cat.name}</span>
                              <span className="text-white font-medium">{formatCurrency(cat.total)}</span>
                            </div>
                            <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
                              <div className={`h-full ${colors[idx % colors.length]} rounded-full transition-all`} style={{ width: `${percentage}%` }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-3">
                <button onClick={() => setShowBillModal(true)} className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg shadow-emerald-500/20">
                  <Plus className="w-4 h-4" />
                  Nova Conta
                </button>
                <button onClick={() => setShowWalletModal(true)} className="flex items-center gap-2 bg-slate-700/50 text-white px-4 py-2 rounded-xl hover:bg-slate-700 transition-all border border-slate-600/50">
                  <CreditCard className="w-4 h-4" />
                  Nova Carteira
                </button>
              </div>
            </div>
          )}

          {/* Bills Tab */}
          {activeTab === 'bills' && (
            <BillsTab
              bills={filteredBills}
              wallets={wallets}
              stats={stats}
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              monthNames={monthNames}
              statusFilter={statusFilter}
              typeFilter={typeFilter}
              searchTerm={searchTerm}
              isSimplifiedMode={isSimplifiedMode}
              hasAnyIncome={bills.some(b => b.type === 'income')}
              onNavigateMonth={navigateMonth}
              onStatusFilterChange={setStatusFilter}
              onTypeFilterChange={setTypeFilter}
              onSearchChange={setSearchTerm}
              onAddBill={() => { setEditingBill(null); setEditingBillType('expense'); setShowBillModal(true); }}
              onAddIncome={() => { setEditingBill(null); setEditingBillType('income'); setShowBillModal(true); }}
              onEditBill={(bill) => { setEditingBill(bill); setEditingBillType(bill.type || 'expense'); setShowBillModal(true); }}
              onDeleteBill={deleteBill}
              onToggleStatus={toggleBillStatus}
              onMarkPaid={openPaymentModal}
              onToggleScheduled={toggleScheduled}
            />
          )}

          {/* Wallets Tab */}
          {activeTab === 'wallets' && (
            <WalletsTab
              wallets={wallets}
              bills={bills}
              onAddWallet={() => { setEditingWallet(null); setShowWalletModal(true); }}
              onEditWallet={(wallet) => { setEditingWallet(wallet); setShowWalletModal(true); }}
              onDeleteWallet={deleteWallet}
              onUpdateBalance={updateWalletBalance}
            />
          )}

          {/* Recurring Tab */}
          {activeTab === 'recurring' && (
            <RecurringTab
              bills={bills.filter(b => b.is_recurring && !b.parent_id)}
              allBills={bills}
              wallets={wallets}
              onAddBill={() => { setEditingBill(null); setShowBillModal(true); }}
              onEditBill={(bill) => { setEditingBill(bill); setShowBillModal(true); }}
              onDeleteBill={deleteBill}
              onToggleStatus={toggleBillStatus}
              onMarkPaid={openPaymentModal}
              onDeleteGroup={deleteRecurringGroup}
            />
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <NotificationsTab
              profile={profile}
              onUpdateProfile={updateProfile}
              supabase={supabase}
              session={session}
            />
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <ProfileTab
              profile={profile}
              email={session.user.email}
              onUpdateProfile={updateProfile}
            />
          )}
        </main>
      </div>

      {/* Modals */}
      {showBillModal && (
        <BillModal
          bill={editingBill}
          billType={editingBillType}
          wallets={wallets}
          categories={categories}
          incomeCategories={incomeCategories}
          saving={savingBill}
          onSave={saveBill}
          onClose={() => { setShowBillModal(false); setEditingBill(null); setEditingBillType('expense'); }}
          onUpload={uploadAttachment}
        />
      )}

      {showWalletModal && (
        <WalletModal
          wallet={editingWallet}
          saving={savingWallet}
          onSave={saveWallet}
          onClose={() => { setShowWalletModal(false); setEditingWallet(null); }}
        />
      )}

      {showPaymentModal && payingBill && (
        <PaymentModal
          bill={payingBill}
          onConfirm={markPaidWithReceipt}
          onClose={() => { setShowPaymentModal(false); setPayingBill(null); }}
          onUpload={uploadAttachment}
        />
      )}

      {/* Modal de Exclusão de Contas Relacionadas */}
      {showDeleteModal && deletingBill && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-slate-700/50">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-400" />
                Excluir Conta Recorrente
              </h2>
              <p className="text-slate-400 text-sm mt-1">
                Esta conta faz parte de uma série recorrente. Escolha quais excluir:
              </p>
            </div>
            
            <div className="p-6 space-y-4 max-h-[50vh] overflow-y-auto">
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedForDelete.includes(deletingBill.id)}
                    onChange={() => {
                      if (selectedForDelete.includes(deletingBill.id)) {
                        setSelectedForDelete(selectedForDelete.filter(id => id !== deletingBill.id));
                      } else {
                        setSelectedForDelete([...selectedForDelete, deletingBill.id]);
                      }
                    }}
                    className="w-5 h-5 rounded border-slate-600 text-red-500 focus:ring-red-500"
                  />
                  <div className="flex-1">
                    <p className="text-white font-medium">{deletingBill.name}</p>
                    <p className="text-slate-500 text-sm">
                      {formatDate(deletingBill.due_date)} • {formatCurrency(deletingBill.amount)}
                      <span className={`ml-2 ${deletingBill.status === 'paid' ? 'text-green-400' : 'text-yellow-400'}`}>
                        ({deletingBill.status === 'paid' ? 'Paga' : 'Pendente'})
                      </span>
                    </p>
                  </div>
                </label>
              </div>
              
              {relatedBills.length > 0 && (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-slate-400 text-sm">Outras parcelas desta conta:</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedForDelete([deletingBill.id, ...relatedBills.map(b => b.id)])}
                        className="text-xs text-emerald-400 hover:text-emerald-300"
                      >
                        Selecionar todas
                      </button>
                      <span className="text-slate-600">|</span>
                      <button
                        onClick={() => setSelectedForDelete([deletingBill.id])}
                        className="text-xs text-slate-400 hover:text-slate-300"
                      >
                        Limpar
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {relatedBills.map(bill => (
                      <div key={bill.id} className={`p-3 rounded-xl border ${selectedForDelete.includes(bill.id) ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-800/30 border-slate-700/30'}`}>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedForDelete.includes(bill.id)}
                            onChange={() => {
                              if (selectedForDelete.includes(bill.id)) {
                                setSelectedForDelete(selectedForDelete.filter(id => id !== bill.id));
                              } else {
                                setSelectedForDelete([...selectedForDelete, bill.id]);
                              }
                            }}
                            className="w-5 h-5 rounded border-slate-600 text-red-500 focus:ring-red-500"
                          />
                          <div className="flex-1 flex items-center justify-between">
                            <div>
                              <p className="text-slate-300 text-sm">{formatDate(bill.due_date)}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${bill.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                {bill.status === 'paid' ? 'Paga' : 'Pendente'}
                              </span>
                              <span className="text-white font-medium">{formatCurrency(bill.amount)}</span>
                            </div>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            <div className="p-6 border-t border-slate-700/50 flex items-center justify-between">
              <p className="text-slate-400 text-sm">
                {selectedForDelete.length} conta{selectedForDelete.length !== 1 ? 's' : ''} selecionada{selectedForDelete.length !== 1 ? 's' : ''}
              </p>
              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingBill(null);
                    setRelatedBills([]);
                    setSelectedForDelete([]);
                  }}
                  className="px-4 py-2 text-slate-400 hover:text-white transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => deleteMultipleBills(selectedForDelete)}
                  disabled={selectedForDelete.length === 0}
                  className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-xl hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir ({selectedForDelete.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================
// SUB COMPONENTS
// =============================================

function LoginForm({ onSubmit, onSwitch, loading }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(email, password); }} className="space-y-4">
      <div>
        <label className="block text-sm text-slate-400 mb-2">Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all" placeholder="seu@email.com" required />
      </div>
      <div>
        <label className="block text-sm text-slate-400 mb-2">Senha</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all" placeholder="••••••••" required />
      </div>
      <button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold py-3 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50 flex items-center justify-center gap-2">
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        Entrar
      </button>
      <p className="text-center text-slate-400 text-sm">
        Não tem conta? <button type="button" onClick={onSwitch} className="text-emerald-400 hover:text-emerald-300 font-medium">Cadastre-se</button>
      </p>
    </form>
  );
}

function RegisterForm({ onSubmit, onSwitch, loading }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('As senhas não coincidem');
      return;
    }
    setError('');
    onSubmit(name, email, password);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">{error}</div>}
      <div>
        <label className="block text-sm text-slate-400 mb-2">Nome</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all" placeholder="Seu nome" required />
      </div>
      <div>
        <label className="block text-sm text-slate-400 mb-2">Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all" placeholder="seu@email.com" required />
      </div>
      <div>
        <label className="block text-sm text-slate-400 mb-2">Senha</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all" placeholder="••••••••" required />
      </div>
      <div>
        <label className="block text-sm text-slate-400 mb-2">Confirmar Senha</label>
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all" placeholder="••••••••" required />
      </div>
      <button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold py-3 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50 flex items-center justify-center gap-2">
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        Criar Conta
      </button>
      <p className="text-center text-slate-400 text-sm">
        Já tem conta? <button type="button" onClick={onSwitch} className="text-emerald-400 hover:text-emerald-300 font-medium">Fazer Login</button>
      </p>
    </form>
  );
}

function StatCard({ icon: Icon, label, value, subtext, color }) {
  const colorClasses = {
    emerald: 'bg-emerald-500/20 text-emerald-400',
    green: 'bg-green-500/20 text-green-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    red: 'bg-red-500/20 text-red-400'
  };
  const valueColors = {
    emerald: 'text-white',
    green: 'text-green-400',
    yellow: 'text-yellow-400',
    red: 'text-red-400'
  };

  return (
    <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/30 rounded-2xl p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-slate-400 text-sm mb-1">{label}</p>
      <p className={`text-xl lg:text-2xl font-bold ${valueColors[color]}`}>{value}</p>
      <p className="text-slate-500 text-xs mt-1">{subtext}</p>
    </div>
  );
}

function BillsTab({ bills, wallets, selectedMonth, selectedYear, monthNames, statusFilter, typeFilter, searchTerm, isSimplifiedMode, hasAnyIncome, onNavigateMonth, onStatusFilterChange, onTypeFilterChange, onSearchChange, onAddBill, onAddIncome, onEditBill, onDeleteBill, onToggleStatus, onMarkPaid, onToggleScheduled }) {
  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  const formatDate = (date) => {
    // Adiciona T00:00:00 para evitar problemas de timezone
    const d = new Date(date + 'T00:00:00');
    return d.toLocaleDateString('pt-BR');
  };
  const [copiedId, setCopiedId] = useState(null);
  const [selectedWallet, setSelectedWallet] = useState('all');

  // Modo simplificado: esconde receitas se não tiver nenhuma cadastrada
  const hideIncomeUI = isSimplifiedMode && !hasAnyIncome;

  const copyPix = (pix, billId) => {
    navigator.clipboard.writeText(pix);
    setCopiedId(billId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Filtrar por carteira
  const filteredBills = selectedWallet === 'all' 
    ? bills 
    : bills.filter(bill => bill.wallet_id === selectedWallet);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">Contas</h1>
          <p className="text-slate-400">{hideIncomeUI ? 'Gerencie suas contas' : 'Gerencie despesas e receitas'}</p>
        </div>
        <div className="flex gap-2 self-start">
          {hideIncomeUI ? (
            // Modo simplificado - apenas botão "Nova Conta"
            <button onClick={onAddBill} className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg shadow-emerald-500/20">
              <Plus className="w-4 h-4" />
              Nova Conta
            </button>
          ) : (
            // Modo normal - botões separados
            <>
              <button onClick={onAddBill} className="flex items-center gap-2 bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-xl hover:from-red-600 hover:to-orange-600 transition-all shadow-lg shadow-red-500/20">
                <TrendingDown className="w-4 h-4" />
                Nova Despesa
              </button>
              <button onClick={onAddIncome} className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg shadow-green-500/20">
                <TrendingUp className="w-4 h-4" />
                Nova Receita
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex items-center gap-2 bg-slate-800/50 rounded-xl p-1 self-start">
          <button onClick={() => onNavigateMonth('prev')} className="p-2 text-slate-400 hover:text-white transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-white font-medium px-4 min-w-[140px] text-center">
            {monthNames[selectedMonth]} {selectedYear}
          </span>
          <button onClick={() => onNavigateMonth('next')} className="p-2 text-slate-400 hover:text-white transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Filtro de Tipo - só mostra se não estiver no modo simplificado */}
        {!hideIncomeUI && (
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'all', label: 'Todas', color: 'emerald' },
              { value: 'expense', label: '💸 Despesas', color: 'red' },
              { value: 'income', label: '💰 Receitas', color: 'green' }
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => onTypeFilterChange(filter.value)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  typeFilter === filter.value
                    ? `bg-${filter.color}-500/20 text-${filter.color}-400 border border-${filter.color}-500/30`
                    : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50 border border-transparent'
                }`}
                style={typeFilter === filter.value ? {
                  backgroundColor: filter.color === 'red' ? 'rgba(239,68,68,0.2)' : filter.color === 'green' ? 'rgba(34,197,94,0.2)' : 'rgba(16,185,129,0.2)',
                  color: filter.color === 'red' ? '#f87171' : filter.color === 'green' ? '#4ade80' : '#34d399',
                  borderColor: filter.color === 'red' ? 'rgba(239,68,68,0.3)' : filter.color === 'green' ? 'rgba(34,197,94,0.3)' : 'rgba(16,185,129,0.3)'
                } : {}}
              >
                {filter.label}
              </button>
            ))}
          </div>
        )}

        {/* Filtro de Status */}
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'Todas' },
            { value: 'pending', label: 'Pendentes' },
            { value: 'paid', label: hideIncomeUI ? 'Pagas' : 'Concluídas' }
          ].map(filter => (
            <button
              key={filter.value}
              onClick={() => onStatusFilterChange(filter.value)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                statusFilter === filter.value
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50 border border-transparent'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Filtro de Carteira */}
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-slate-400" />
          <select
            value={selectedWallet}
            onChange={(e) => setSelectedWallet(e.target.value)}
            className="bg-slate-800/50 border border-slate-700 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-emerald-500 transition-all text-sm"
          >
            <option value="all">Todas as Carteiras</option>
            {wallets.map(wallet => (
              <option key={wallet.id} value={wallet.id}>{wallet.name}</option>
            ))}
          </select>
        </div>

        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            placeholder="Buscar conta..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full bg-slate-800/50 border border-slate-700 rounded-xl pl-10 pr-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all"
          />
        </div>
      </div>

      {/* Bills List */}
      <div className="space-y-3">
        {filteredBills.length === 0 ? (
          <div className="text-center py-12 bg-slate-800/30 rounded-2xl border border-slate-700/30">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Nenhuma conta encontrada</p>
            <button onClick={onAddBill} className="mt-4 text-emerald-400 hover:text-emerald-300 font-medium text-sm">
              Adicionar primeira conta
            </button>
          </div>
        ) : (
          filteredBills.map(bill => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const dueDate = new Date(bill.due_date + 'T00:00:00');
            const isOverdue = bill.status === 'pending' && dueDate < today;
            const CategoryIcon = getCategoryIcon(bill.category, bill.icon);
            
            // Calcular tempo até próxima notificação
            const getNextNotificationTime = () => {
              if (!bill.notification_enabled || bill.status === 'paid') return null;
              const now = new Date();
              const notifyDate = new Date(bill.due_date + 'T00:00:00');
              notifyDate.setDate(notifyDate.getDate() - (bill.notification_days_before || 3));
              
              // Horários de notificação: 8h, 12h, 18h
              const notifyHours = [8, 12, 18];
              
              // Se já passou o período de notificação
              const overdueLimit = new Date(bill.due_date + 'T00:00:00');
              overdueLimit.setDate(overdueLimit.getDate() + 3);
              if (now > overdueLimit) return null;
              
              // Se ainda não chegou o período de notificação
              if (now < notifyDate) {
                const diffDays = Math.ceil((notifyDate - now) / (1000 * 60 * 60 * 24));
                return `em ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
              }
              
              // Encontrar próximo horário
              const currentHour = now.getHours();
              for (const hour of notifyHours) {
                if (hour > currentHour) {
                  return `às ${hour}h`;
                }
              }
              return 'amanhã às 8h';
            };
            
            const nextNotification = getNextNotificationTime();
            
            return (
              <div
                key={bill.id}
                className={`bg-slate-800/30 backdrop-blur-sm border rounded-2xl p-4 lg:p-5 transition-all hover:bg-slate-800/50 ${
                  isOverdue ? 'border-red-500/30' : bill.status === 'paid' ? 'border-green-500/30' : 'border-slate-700/30'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                      isOverdue ? 'bg-red-500/20' : bill.status === 'paid' ? 'bg-green-500/20' : `bg-${getCategoryColor(bill.category)}-500/20`
                    }`}>
                      {bill.status === 'paid' ? (
                        <CheckCircle className="w-6 h-6 text-green-400" />
                      ) : isOverdue ? (
                        <AlertCircle className="w-6 h-6 text-red-400" />
                      ) : (
                        <CategoryIcon className={`w-6 h-6 text-${getCategoryColor(bill.category)}-400`} />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-white font-semibold">{bill.name}</h3>
                        {/* Indicador de Receita/Despesa - só mostra se não for modo simplificado ou se for receita */}
                        {!hideIncomeUI && (
                          bill.type === 'income' ? (
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" />
                              Receita
                            </span>
                          ) : (
                            <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <TrendingDown className="w-3 h-3" />
                              Despesa
                            </span>
                          )
                        )}
                        {bill.is_recurring && (
                          <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Repeat className="w-3 h-3" />
                            Recorrente
                          </span>
                        )}
                        {bill.scheduled && bill.status !== 'paid' && (
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Agendado
                          </span>
                        )}
                        {bill.notification_enabled && nextNotification && (
                          <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full flex items-center gap-1" title={`Próxima notificação ${nextNotification}`}>
                            <Bell className="w-3 h-3" />
                            {nextNotification}
                          </span>
                        )}
                        {bill.attachment_url && (
                          <a href={bill.attachment_url} target="_blank" rel="noopener noreferrer" className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full flex items-center gap-1 hover:bg-purple-500/30">
                            <FileText className="w-3 h-3" />
                            Boleto
                          </a>
                        )}
                        {bill.receipt_url && (
                          <a href={bill.receipt_url} target="_blank" rel="noopener noreferrer" className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1 hover:bg-green-500/30">
                            <Image className="w-3 h-3" />
                            Comprovante
                          </a>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-400 flex-wrap">
                        <span>{bill.category}</span>
                        <span>•</span>
                        <span>{bill.type === 'income' ? 'Receber:' : 'Vence:'} {formatDate(bill.due_date)}</span>
                        {bill.payer && (
                          <>
                            <span>•</span>
                            <span className="text-blue-400">De: {bill.payer}</span>
                          </>
                        )}
                        {bill.status === 'paid' && bill.paid_date && (
                          <>
                            <span>•</span>
                            <span className="text-green-400">{bill.type === 'income' ? 'Recebido:' : 'Pago:'} {formatDate(bill.paid_date)}</span>
                          </>
                        )}
                      </div>
                      {bill.notes && <p className="text-slate-500 text-sm mt-2">{bill.notes}</p>}
                      
                      {/* PIX Code - Completo com scroll */}
                      {bill.pix_code && (
                        <div className="mt-3 p-3 bg-slate-700/30 rounded-lg max-w-full overflow-hidden">
                          <div className="flex flex-col gap-2">
                            <div className="overflow-x-auto max-w-full">
                              <p className="text-xs text-slate-300 font-mono break-all select-all">{bill.pix_code}</p>
                            </div>
                            <button
                              onClick={() => copyPix(bill.pix_code, bill.id)}
                              className={`flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all w-full ${
                                copiedId === bill.id 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                              }`}
                            >
                              <Copy className="w-3 h-3" />
                              {copiedId === bill.id ? 'Copiado!' : 'Copiar PIX'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 justify-between lg:justify-end">
                    <span className={`text-xl font-bold ${isOverdue ? 'text-red-400' : bill.status === 'paid' ? 'text-green-400' : 'text-white'}`}>
                      {formatCurrency(bill.amount)}
                    </span>
                    
                    <div className="flex items-center gap-2">
                      {bill.status !== 'paid' && (
                        <>
                          <button
                            onClick={() => onToggleScheduled(bill)}
                            className={`p-2 rounded-lg transition-all ${
                              bill.scheduled 
                                ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' 
                                : 'bg-slate-700/50 text-slate-400 hover:bg-blue-500/20 hover:text-blue-400'
                            }`}
                            title={bill.scheduled ? 'Remover agendamento' : 'Marcar como agendado'}
                          >
                            <Calendar className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onMarkPaid(bill)}
                            className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-all"
                            title="Marcar como pago"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {bill.status === 'paid' && (
                        <button
                          onClick={() => onToggleStatus(bill)}
                          className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-all"
                          title="Marcar como pendente"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => onEditBill(bill)} className="p-2 bg-slate-700/50 text-slate-400 rounded-lg hover:bg-slate-700 hover:text-white transition-all" title="Editar">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => onDeleteBill(bill.id)} className="p-2 bg-slate-700/50 text-slate-400 rounded-lg hover:bg-red-500/20 hover:text-red-400 transition-all" title="Excluir">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function WalletsTab({ wallets, bills, onAddWallet, onEditWallet, onDeleteWallet, onUpdateBalance }) {
  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [selectedWalletForBalance, setSelectedWalletForBalance] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceOperation, setBalanceOperation] = useState('add'); // 'add' ou 'set'

  const openBalanceModal = (wallet) => {
    setSelectedWalletForBalance(wallet);
    setBalanceAmount('');
    setBalanceOperation('add');
    setShowBalanceModal(true);
  };

  const handleBalanceSubmit = () => {
    if (!balanceAmount || isNaN(parseFloat(balanceAmount))) return;
    
    const amount = parseFloat(balanceAmount);
    const newBalance = balanceOperation === 'add' 
      ? (selectedWalletForBalance.balance || 0) + amount
      : amount;
    
    onUpdateBalance(selectedWalletForBalance.id, newBalance);
    setShowBalanceModal(false);
  };

  // Calcular totais gerais
  const totalBalance = wallets.reduce((acc, w) => acc + (w.balance || 0), 0);
  const totalPending = bills.filter(b => b.status === 'pending').reduce((acc, b) => acc + parseFloat(b.amount), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">Carteiras</h1>
          <p className="text-slate-400">Gerencie suas carteiras e saldos</p>
        </div>
        <button onClick={onAddWallet} className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg shadow-emerald-500/20 self-start">
          <Plus className="w-4 h-4" />
          Nova Carteira
        </button>
      </div>

      {/* Resumo Geral de Saldos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Wallet className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="text-slate-400 text-sm">Saldo Total</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400">{formatCurrency(totalBalance)}</p>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <span className="text-slate-400 text-sm">Total Pendente</span>
          </div>
          <p className="text-2xl font-bold text-yellow-400">{formatCurrency(totalPending)}</p>
        </div>
        
        <div className={`bg-gradient-to-br ${totalBalance >= totalPending ? 'from-green-500/20 to-emerald-500/20 border-green-500/30' : 'from-red-500/20 to-orange-500/20 border-red-500/30'} border rounded-2xl p-5`}>
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-10 h-10 ${totalBalance >= totalPending ? 'bg-green-500/20' : 'bg-red-500/20'} rounded-xl flex items-center justify-center`}>
              {totalBalance >= totalPending ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-400" />
              )}
            </div>
            <span className="text-slate-400 text-sm">{totalBalance >= totalPending ? 'Saldo Disponível' : 'Falta para Cobrir'}</span>
          </div>
          <p className={`text-2xl font-bold ${totalBalance >= totalPending ? 'text-green-400' : 'text-red-400'}`}>
            {formatCurrency(Math.abs(totalBalance - totalPending))}
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {wallets.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-slate-800/30 rounded-2xl border border-slate-700/30">
            <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Nenhuma carteira cadastrada</p>
            <button onClick={onAddWallet} className="mt-4 text-emerald-400 hover:text-emerald-300 font-medium text-sm">
              Adicionar primeira carteira
            </button>
          </div>
        ) : (
          wallets.map(wallet => {
            const walletBills = bills.filter(b => b.wallet_id === wallet.id);
            const pendingBills = walletBills.filter(b => b.status === 'pending');
            const pendingTotal = pendingBills.reduce((acc, b) => acc + parseFloat(b.amount), 0);
            const paidTotal = walletBills.filter(b => b.status === 'paid').reduce((acc, b) => acc + parseFloat(b.amount), 0);
            const balance = wallet.balance || 0;
            const remaining = balance - pendingTotal;
            
            return (
              <div key={wallet.id} className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/30 rounded-2xl p-5 hover:bg-slate-800/50 transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${wallet.color}20` }}>
                    <CreditCard className="w-6 h-6" style={{ color: wallet.color }} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => onEditWallet(wallet)} className="p-2 text-slate-400 hover:text-white transition-all" title="Editar">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDeleteWallet(wallet.id)} className="p-2 text-slate-400 hover:text-red-400 transition-all" title="Excluir">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <h3 className="text-white font-semibold text-lg mb-1">{wallet.name}</h3>
                
                {/* Saldo da Carteira */}
                <div className="my-4 p-3 bg-slate-700/30 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-400 text-sm">Saldo Disponível</span>
                    <button 
                      onClick={() => openBalanceModal(wallet)}
                      className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-lg hover:bg-emerald-500/30 transition-all flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Adicionar
                    </button>
                  </div>
                  <p className="text-2xl font-bold text-emerald-400">{formatCurrency(balance)}</p>
                </div>
                
                <div className="space-y-2 pt-4 border-t border-slate-700/50">
                  <div className="flex justify-between text-sm">
                    <span className="text-yellow-400">Pendente</span>
                    <span className="text-yellow-400 font-medium">{formatCurrency(pendingTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-green-400">Já pago</span>
                    <span className="text-green-400 font-medium">{formatCurrency(paidTotal)}</span>
                  </div>
                  
                  {/* Indicador de cobertura */}
                  <div className={`mt-3 p-2 rounded-lg text-sm ${remaining >= 0 ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex justify-between items-center">
                      <span className={remaining >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {remaining >= 0 ? '✓ Sobra após pagar:' : '⚠ Falta para cobrir:'}
                      </span>
                      <span className={`font-bold ${remaining >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(Math.abs(remaining))}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-xs text-slate-500 mt-2">{pendingBills.length} conta{pendingBills.length !== 1 ? 's' : ''} pendente{pendingBills.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal de Adicionar Saldo */}
      {showBalanceModal && selectedWalletForBalance && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-slate-700/50">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Wallet className="w-5 h-5 text-emerald-400" />
                Gerenciar Saldo
              </h2>
              <p className="text-slate-400 text-sm mt-1">{selectedWalletForBalance.name}</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="p-4 bg-slate-800/50 rounded-xl text-center">
                <p className="text-slate-400 text-sm mb-1">Saldo Atual</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {formatCurrency(selectedWalletForBalance.balance || 0)}
                </p>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setBalanceOperation('add')}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${
                    balanceOperation === 'add'
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-slate-800/50 text-slate-400 border border-transparent'
                  }`}
                >
                  + Adicionar ao Saldo
                </button>
                <button
                  onClick={() => setBalanceOperation('set')}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${
                    balanceOperation === 'set'
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      : 'bg-slate-800/50 text-slate-400 border border-transparent'
                  }`}
                >
                  = Definir Novo Saldo
                </button>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  {balanceOperation === 'add' ? 'Valor a Adicionar' : 'Novo Saldo'}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">R$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={balanceAmount}
                    onChange={(e) => setBalanceAmount(e.target.value)}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-xl pl-12 pr-4 py-3 text-white text-lg font-semibold placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all"
                    placeholder="0,00"
                    autoFocus
                  />
                </div>
              </div>
              
              {balanceAmount && !isNaN(parseFloat(balanceAmount)) && (
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                  <p className="text-emerald-400 text-sm">
                    Novo saldo será: <span className="font-bold">
                      {formatCurrency(
                        balanceOperation === 'add'
                          ? (selectedWalletForBalance.balance || 0) + parseFloat(balanceAmount)
                          : parseFloat(balanceAmount)
                      )}
                    </span>
                  </p>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-slate-700/50 flex gap-3 justify-end">
              <button 
                onClick={() => setShowBalanceModal(false)} 
                className="px-4 py-2 text-slate-400 hover:text-white transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleBalanceSubmit}
                disabled={!balanceAmount || isNaN(parseFloat(balanceAmount))}
                className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-2 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" />
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RecurringTab({ bills, allBills, wallets, onAddBill, onEditBill, onDeleteBill, onToggleStatus, onMarkPaid, onDeleteGroup }) {
  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  const formatDate = (date) => {
    const d = new Date(date + 'T00:00:00');
    return d.toLocaleDateString('pt-BR');
  };
  const recurrenceLabels = { weekly: 'Semanal', monthly: 'Mensal', quarterly: 'Trimestral', yearly: 'Anual' };
  
  const [selectedWallet, setSelectedWallet] = useState('all');
  const [expandedBill, setExpandedBill] = useState(null);
  const [showDeleteOptions, setShowDeleteOptions] = useState(null);

  // Agrupar contas recorrentes por nome e carteira
  const getRecurringGroups = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Filtrar por carteira
    const filteredBills = selectedWallet === 'all' 
      ? allBills 
      : allBills.filter(b => b.wallet_id === selectedWallet);
    
    // Agrupar contas recorrentes pelo nome (considerando que têm o mesmo nome)
    const groups = {};
    
    filteredBills.filter(b => b.is_recurring).forEach(bill => {
      const key = `${bill.name}-${bill.wallet_id || 'none'}`;
      if (!groups[key]) {
        groups[key] = {
          name: bill.name,
          wallet_id: bill.wallet_id,
          category: bill.category,
          amount: bill.amount,
          recurrence_type: bill.recurrence_type,
          icon: bill.icon,
          parcelas: []
        };
      }
      groups[key].parcelas.push(bill);
    });
    
    // Ordenar parcelas por data
    return Object.values(groups)
      .map(group => {
        group.parcelas.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
        const pendingCount = group.parcelas.filter(p => p.status === 'pending').length;
        const paidCount = group.parcelas.filter(p => p.status === 'paid').length;
        const overdueCount = group.parcelas.filter(p => {
          const dueDate = new Date(p.due_date + 'T00:00:00');
          return p.status === 'pending' && dueDate < today;
        }).length;
        const nextDue = group.parcelas.find(p => p.status === 'pending');
        
        return {
          ...group,
          pendingCount,
          paidCount,
          overdueCount,
          totalCount: group.parcelas.length,
          nextDue
        };
      })
      .filter(group => group.totalCount > 0)
      .sort((a, b) => {
        // Ordenar por próximo vencimento
        if (a.nextDue && b.nextDue) {
          return new Date(a.nextDue.due_date) - new Date(b.nextDue.due_date);
        }
        if (a.nextDue) return -1;
        if (b.nextDue) return 1;
        return 0;
      });
  };

  const recurringGroups = getRecurringGroups();

  // Contar por carteira
  const getWalletCounts = () => {
    const counts = {};
    allBills.filter(b => b.is_recurring).forEach(bill => {
      const walletId = bill.wallet_id || 'none';
      counts[walletId] = (counts[walletId] || 0) + 1;
    });
    return counts;
  };
  const walletCounts = getWalletCounts();

  const getParcelaStatus = (parcela) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(parcela.due_date + 'T00:00:00');
    
    if (parcela.status === 'paid') return 'paid';
    if (dueDate < today) return 'overdue';
    return 'pending';
  };

  const handleDeleteGroup = (group, option) => {
    if (window.confirm(
      option === 'all' 
        ? `Excluir TODAS as ${group.totalCount} parcelas de "${group.name}"?`
        : option === 'pending'
        ? `Excluir as ${group.pendingCount} parcelas PENDENTES de "${group.name}"? (${group.paidCount} pagas serão mantidas)`
        : `Excluir as ${group.paidCount} parcelas PAGAS de "${group.name}"?`
    )) {
      onDeleteGroup(group.name, group.wallet_id, option);
    }
    setShowDeleteOptions(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">Contas Recorrentes</h1>
          <p className="text-slate-400">Contas que se repetem automaticamente</p>
        </div>
        <button onClick={onAddBill} className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg shadow-emerald-500/20 self-start">
          <Plus className="w-4 h-4" />
          Nova Recorrente
        </button>
      </div>

      {/* Filtro por Carteira */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedWallet('all')}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
            selectedWallet === 'all'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50 border border-transparent'
          }`}
        >
          <Wallet className="w-4 h-4" />
          Todas
          <span className="text-xs bg-slate-700 px-1.5 py-0.5 rounded">
            {Object.values(walletCounts).reduce((a, b) => a + b, 0)}
          </span>
        </button>
        
        {wallets.map(wallet => (
          <button
            key={wallet.id}
            onClick={() => setSelectedWallet(wallet.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
              selectedWallet === wallet.id
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50 border border-transparent'
            }`}
          >
            <CreditCard className="w-4 h-4" />
            {wallet.name}
            {walletCounts[wallet.id] && (
              <span className="text-xs bg-slate-700 px-1.5 py-0.5 rounded">{walletCounts[wallet.id]}</span>
            )}
          </button>
        ))}
        
        {walletCounts['none'] > 0 && (
          <button
            onClick={() => setSelectedWallet('none')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
              selectedWallet === 'none'
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50 border border-transparent'
            }`}
          >
            <FileText className="w-4 h-4" />
            Sem Carteira
            <span className="text-xs bg-slate-700 px-1.5 py-0.5 rounded">{walletCounts['none']}</span>
          </button>
        )}
      </div>

      <div className="space-y-3">
        {recurringGroups.length === 0 ? (
          <div className="text-center py-12 bg-slate-800/30 rounded-2xl border border-slate-700/30">
            <Repeat className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Nenhuma conta recorrente pendente</p>
            <p className="text-slate-500 text-sm mt-2">Todas as parcelas foram pagas ou não há recorrentes nesta carteira</p>
          </div>
        ) : (
          recurringGroups.map((group, idx) => {
            const isExpanded = expandedBill === `${group.name}-${group.wallet_id}`;
            const CategoryIcon = getCategoryIcon(group.category, group.icon);
            
            return (
              <div key={idx} className="bg-slate-800/30 backdrop-blur-sm border border-emerald-500/30 rounded-2xl overflow-hidden">
                {/* Header - Clicável */}
                <div 
                  onClick={() => setExpandedBill(isExpanded ? null : `${group.name}-${group.wallet_id}`)}
                  className="p-5 cursor-pointer hover:bg-slate-700/20 transition-all"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center shrink-0">
                        <CategoryIcon className="w-6 h-6 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold flex items-center gap-2">
                          {group.name}
                          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </h3>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-400 flex-wrap">
                          <span className="bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full text-xs">
                            {recurrenceLabels[group.recurrence_type]}
                          </span>
                          <span>{group.category}</span>
                          <span>•</span>
                          <span className="text-green-400">{group.paidCount} pagas</span>
                          <span className="text-yellow-400">{group.pendingCount - group.overdueCount} pendentes</span>
                          {group.overdueCount > 0 && (
                            <span className="text-red-400">{group.overdueCount} vencidas</span>
                          )}
                        </div>
                        {group.nextDue && (
                          <p className="text-xs text-slate-500 mt-1">
                            Próximo vencimento: {formatDate(group.nextDue.due_date)}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4 justify-between lg:justify-end">
                      <div className="text-right">
                        <span className="text-xl font-bold text-white">{formatCurrency(group.amount)}</span>
                        <p className="text-xs text-slate-500">/parcela</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Lista de Parcelas - Expandida */}
                {isExpanded && (
                  <div className="border-t border-slate-700/50 bg-slate-900/30">
                    <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
                      {group.parcelas.map((parcela, pIdx) => {
                        const status = getParcelaStatus(parcela);
                        return (
                          <div 
                            key={parcela.id}
                            className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                              status === 'paid' ? 'bg-green-500/10 border border-green-500/30' :
                              status === 'overdue' ? 'bg-red-500/10 border border-red-500/30' :
                              'bg-slate-700/30 border border-slate-600/30'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                status === 'paid' ? 'bg-green-500/20' :
                                status === 'overdue' ? 'bg-red-500/20' :
                                'bg-yellow-500/20'
                              }`}>
                                {status === 'paid' ? (
                                  <CheckCircle className="w-4 h-4 text-green-400" />
                                ) : status === 'overdue' ? (
                                  <AlertCircle className="w-4 h-4 text-red-400" />
                                ) : (
                                  <Clock className="w-4 h-4 text-yellow-400" />
                                )}
                              </div>
                              <div>
                                <p className={`font-medium text-sm ${
                                  status === 'paid' ? 'text-green-400' :
                                  status === 'overdue' ? 'text-red-400' :
                                  'text-white'
                                }`}>
                                  Parcela {pIdx + 1}/{group.totalCount}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {formatDate(parcela.due_date)}
                                  {status === 'paid' && parcela.paid_date && ` • Pago em ${formatDate(parcela.paid_date)}`}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-3">
                              <span className={`font-semibold ${
                                status === 'paid' ? 'text-green-400' :
                                status === 'overdue' ? 'text-red-400' :
                                'text-white'
                              }`}>
                                {formatCurrency(parcela.amount)}
                              </span>
                              
                              {status !== 'paid' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onMarkPaid(parcela);
                                  }}
                                  className="p-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-all"
                                  title="Marcar como pago"
                                >
                                  <CheckCircle className="w-3.5 h-3.5" />
                                </button>
                              )}
                              
                              {status === 'paid' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleStatus(parcela);
                                  }}
                                  className="p-1.5 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-all"
                                  title="Marcar como pendente"
                                >
                                  <XCircle className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Botões de Exclusão */}
                    <div className="p-4 border-t border-slate-700/50 flex flex-wrap gap-2">
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDeleteOptions(showDeleteOptions === `${group.name}-${group.wallet_id}` ? null : `${group.name}-${group.wallet_id}`);
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-all text-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                          Excluir Parcelas
                        </button>
                        
                        {showDeleteOptions === `${group.name}-${group.wallet_id}` && (
                          <div className="absolute bottom-full left-0 mb-2 bg-slate-800 border border-slate-700 rounded-xl shadow-xl p-2 min-w-[200px] z-10">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteGroup(group, 'all');
                              }}
                              className="w-full text-left px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-lg text-sm flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              Excluir TODAS ({group.totalCount})
                            </button>
                            {group.pendingCount > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteGroup(group, 'pending');
                                }}
                                className="w-full text-left px-3 py-2 text-yellow-400 hover:bg-yellow-500/10 rounded-lg text-sm flex items-center gap-2"
                              >
                                <Clock className="w-4 h-4" />
                                Só pendentes ({group.pendingCount})
                              </button>
                            )}
                            {group.paidCount > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteGroup(group, 'paid');
                                }}
                                className="w-full text-left px-3 py-2 text-green-400 hover:bg-green-500/10 rounded-lg text-sm flex items-center gap-2"
                              >
                                <CheckCircle className="w-4 h-4" />
                                Só pagas ({group.paidCount})
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function NotificationsTab({ profile, onUpdateProfile, supabase, session }) {
  const [form, setForm] = useState({
    notification_message: profile?.notification_message || 'Lembrete: A conta {nome} vence em {dias} dias no valor de {valor}!',
    notification_message_overdue: profile?.notification_message_overdue || 'URGENTE: A conta {nome} VENCEU em {vencimento} no valor de {valor}! Regularize o pagamento.',
    notification_message_scheduled: profile?.notification_message_scheduled || '📅 AGENDADO: Verifique se a conta {nome} no valor de {valor} já foi debitada! Vencimento: {vencimento}',
    push_enabled: profile?.push_enabled || false
  });
  const [testingPush, setTestingPush] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testType, setTestType] = useState('normal');
  const [pushStatus, setPushStatus] = useState('checking'); // 'checking', 'subscribed', 'not-subscribed', 'denied', 'unsupported'
  const [playerId, setPlayerId] = useState(profile?.onesignal_player_id || null);

  // Verificar status das notificações ao carregar
  useEffect(() => {
    checkPushStatus();
  }, []);

  const checkPushStatus = async () => {
    if (typeof window === 'undefined' || !window.OneSignal) {
      setPushStatus('unsupported');
      return;
    }

    try {
      const permission = await window.OneSignal.Notifications.permission;
      const isSubscribed = await window.OneSignal.User.PushSubscription.optedIn;
      
      if (permission === false) {
        setPushStatus('denied');
      } else if (isSubscribed) {
        setPushStatus('subscribed');
        // Salvar player ID
        const id = await window.OneSignal.User.PushSubscription.id;
        if (id && id !== playerId) {
          setPlayerId(id);
          // Salvar no banco
          await supabase.from('profiles').update({ onesignal_player_id: id }).eq('id', session.user.id);
        }
      } else {
        setPushStatus('not-subscribed');
      }
    } catch (error) {
      console.error('Erro ao verificar push:', error);
      setPushStatus('unsupported');
    }
  };

  const subscribeToPush = async () => {
    if (!window.OneSignal) return;
    
    try {
      await window.OneSignal.Slidedown.promptPush();
      // Aguardar um pouco e verificar novamente
      setTimeout(checkPushStatus, 2000);
    } catch (error) {
      console.error('Erro ao inscrever:', error);
    }
  };

  const testPushNotification = async (type = 'normal') => {
    if (!playerId) {
      alert('Você precisa ativar as notificações primeiro!');
      return;
    }

    setTestingPush(true);
    setTestResult(null);
    setTestType(type);

    try {
      const response = await fetch(`/api/send-notifications?test=true&type=${type}&playerId=${playerId}`);
      const data = await response.json();
      
      if (data.success) {
        setTestResult({ success: true, message: 'Notificação enviada! Verifique seu dispositivo.' });
      } else {
        setTestResult({ success: false, message: data.error || 'Erro ao enviar notificação' });
      }
    } catch (error) {
      setTestResult({ success: false, message: 'Erro de conexão: ' + error.message });
    }

    setTestingPush(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-white">Notificações Push</h1>
        <p className="text-slate-400">Configure os lembretes das suas contas</p>
      </div>

      {/* Info sobre horários */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Bell className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <p className="text-blue-400 font-medium">Horários de Notificação</p>
            <p className="text-slate-400 text-sm mt-1">
              As notificações são enviadas automaticamente às <strong className="text-white">8h</strong>, <strong className="text-white">12h</strong> e <strong className="text-white">18h</strong>.
              Contas vencidas continuam recebendo lembretes por até 3 dias após o vencimento.
            </p>
          </div>
        </div>
      </div>

      {/* Status e Ativação de Push */}
      <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/30 rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-emerald-400" />
          Status das Notificações
        </h2>
        
        <div className="space-y-4">
          {/* Status */}
          <div className="p-4 rounded-xl border flex items-center justify-between
            ${pushStatus === 'subscribed' ? 'bg-green-500/10 border-green-500/30' : 
              pushStatus === 'denied' ? 'bg-red-500/10 border-red-500/30' : 
              'bg-yellow-500/10 border-yellow-500/30'}">
            <div className="flex items-center gap-3">
              {pushStatus === 'subscribed' ? (
                <CheckCircle className="w-6 h-6 text-green-400" />
              ) : pushStatus === 'denied' ? (
                <XCircle className="w-6 h-6 text-red-400" />
              ) : pushStatus === 'checking' ? (
                <Loader2 className="w-6 h-6 text-yellow-400 animate-spin" />
              ) : (
                <AlertCircle className="w-6 h-6 text-yellow-400" />
              )}
              <div>
                <p className="font-medium text-white">
                  {pushStatus === 'subscribed' ? 'Notificações Ativas' :
                   pushStatus === 'denied' ? 'Notificações Bloqueadas' :
                   pushStatus === 'checking' ? 'Verificando...' :
                   pushStatus === 'unsupported' ? 'Navegador não suportado' :
                   'Notificações Desativadas'}
                </p>
                <p className="text-sm text-slate-400">
                  {pushStatus === 'subscribed' ? 'Você receberá alertas neste dispositivo' :
                   pushStatus === 'denied' ? 'Desbloqueie nas configurações do navegador' :
                   pushStatus === 'checking' ? 'Aguarde...' :
                   pushStatus === 'unsupported' ? 'Use Chrome, Firefox ou Edge' :
                   'Clique no botão para ativar'}
                </p>
              </div>
            </div>
            
            {pushStatus === 'not-subscribed' && (
              <button
                onClick={subscribeToPush}
                className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all"
              >
                Ativar Agora
              </button>
            )}
          </div>

          {/* Toggle de notificações automáticas */}
          <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
            <div>
              <p className="text-white font-medium">Receber Lembretes Automáticos</p>
              <p className="text-slate-500 text-sm">Notificações às 8h, 12h e 18h</p>
            </div>
            <button
              onClick={() => setForm({...form, push_enabled: !form.push_enabled})}
              className={`w-12 h-6 rounded-full transition-all ${form.push_enabled ? 'bg-emerald-500' : 'bg-slate-600'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-all ${form.push_enabled ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
            </button>
          </div>

          {/* Botões de Teste */}
          {pushStatus === 'subscribed' && (
            <div className="pt-2 space-y-2">
              <button
                onClick={() => testPushNotification('normal')}
                disabled={testingPush}
                className="w-full flex items-center justify-center gap-2 bg-blue-500/20 text-blue-400 border border-blue-500/30 px-4 py-3 rounded-xl hover:bg-blue-500/30 transition-all disabled:opacity-50"
              >
                {testingPush && testType === 'normal' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4" />
                    Testar Lembrete Normal
                  </>
                )}
              </button>

              <button
                onClick={() => testPushNotification('overdue')}
                disabled={testingPush}
                className="w-full flex items-center justify-center gap-2 bg-red-500/20 text-red-400 border border-red-500/30 px-4 py-3 rounded-xl hover:bg-red-500/30 transition-all disabled:opacity-50"
              >
                {testingPush && testType === 'overdue' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4" />
                    Testar Alerta de Vencida
                  </>
                )}
              </button>
              
              {testResult && (
                <div className={`mt-3 p-3 rounded-lg text-sm ${testResult.success ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                  {testResult.success ? '✅ ' : '❌ '}{testResult.message}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mensagem para Contas a Vencer */}
      <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/30 rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-emerald-400" />
          Mensagem para Contas a Vencer
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Modelo da mensagem</label>
            <textarea
              value={form.notification_message}
              onChange={(e) => setForm({...form, notification_message: e.target.value})}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all h-24 resize-none"
              placeholder="Use as variáveis disponíveis para personalizar"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-slate-500">Variáveis:</span>
            {['{nome}', '{valor}', '{dias}', '{vencimento}', '{categoria}'].map(v => (
              <button
                key={v}
                onClick={() => setForm({...form, notification_message: form.notification_message + ' ' + v})}
                className="text-xs bg-slate-700/50 text-slate-300 px-2 py-1 rounded hover:bg-slate-700 transition-all"
              >
                {v}
              </button>
            ))}
          </div>

          <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
            <p className="text-emerald-400 text-sm mb-2">📱 Prévia:</p>
            <p className="text-white">
              {form.notification_message
                .replace('{nome}', 'Conta de Luz')
                .replace('{valor}', 'R$ 150,00')
                .replace('{dias}', '3')
                .replace('{vencimento}', '15/01/2025')
                .replace('{categoria}', 'Moradia')}
            </p>
          </div>
        </div>
      </div>

      {/* Mensagem para Contas Vencidas */}
      <div className="bg-slate-800/30 backdrop-blur-sm border border-red-500/30 rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-400" />
          Mensagem para Contas Vencidas
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Modelo da mensagem (enviada por até 3 dias após vencer)</label>
            <textarea
              value={form.notification_message_overdue}
              onChange={(e) => setForm({...form, notification_message_overdue: e.target.value})}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-red-500 transition-all h-24 resize-none"
              placeholder="Mensagem para contas vencidas"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-slate-500">Variáveis:</span>
            {['{nome}', '{valor}', '{dias}', '{vencimento}', '{categoria}'].map(v => (
              <button
                key={v}
                onClick={() => setForm({...form, notification_message_overdue: form.notification_message_overdue + ' ' + v})}
                className="text-xs bg-slate-700/50 text-slate-300 px-2 py-1 rounded hover:bg-slate-700 transition-all"
              >
                {v}
              </button>
            ))}
          </div>

          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <p className="text-red-400 text-sm mb-2">🚨 Prévia (conta vencida):</p>
            <p className="text-white">
              {form.notification_message_overdue
                .replace('{nome}', 'Conta de Luz')
                .replace('{valor}', 'R$ 150,00')
                .replace('{dias}', '2')
                .replace('{vencimento}', '13/01/2025')
                .replace('{categoria}', 'Moradia')}
            </p>
          </div>
        </div>
      </div>

      {/* Mensagem para Contas Agendadas */}
      <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/30 rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-400" />
          Mensagem para Contas Agendadas
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Modelo da mensagem (para verificar se já foi debitado)</label>
            <textarea
              value={form.notification_message_scheduled}
              onChange={(e) => setForm({...form, notification_message_scheduled: e.target.value})}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-all h-24 resize-none"
              placeholder="Mensagem para contas agendadas"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-slate-500">Variáveis:</span>
            {['{nome}', '{valor}', '{dias}', '{vencimento}', '{categoria}'].map(v => (
              <button
                key={v}
                onClick={() => setForm({...form, notification_message_scheduled: form.notification_message_scheduled + ' ' + v})}
                className="text-xs bg-slate-700/50 text-slate-300 px-2 py-1 rounded hover:bg-slate-700 transition-all"
              >
                {v}
              </button>
            ))}
          </div>

          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
            <p className="text-blue-400 text-sm mb-2">📅 Prévia (conta agendada):</p>
            <p className="text-white">
              {form.notification_message_scheduled
                .replace('{nome}', 'Conta de Luz')
                .replace('{valor}', 'R$ 150,00')
                .replace('{dias}', '2')
                .replace('{vencimento}', '13/01/2025')
                .replace('{categoria}', 'Moradia')}
            </p>
          </div>
        </div>
      </div>

      <button
        onClick={() => onUpdateProfile(form)}
        className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-3 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg shadow-emerald-500/20"
      >
        <Save className="w-4 h-4" />
        Salvar Configurações
      </button>
    </div>
  );
}

function ProfileTab({ profile, email, onUpdateProfile }) {
  const [form, setForm] = useState({ name: profile?.name || '' });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-white">Meu Perfil</h1>
        <p className="text-slate-400">Gerencie suas informações pessoais</p>
      </div>

      <div className="max-w-2xl">
        <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/30 rounded-2xl p-6">
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-700/50">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center text-2xl font-bold text-slate-900">
              {(profile?.name || 'U').charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">{profile?.name || 'Usuário'}</h2>
              <p className="text-slate-400">{email}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Nome</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-all"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-2">Email</label>
              <input
                type="email"
                value={email}
                disabled
                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-slate-500 cursor-not-allowed"
              />
              <p className="text-slate-500 text-xs mt-1">Email não pode ser alterado</p>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => onUpdateProfile(form)}
              className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-3 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg shadow-emerald-500/20"
            >
              <Save className="w-4 h-4" />
              Salvar Alterações
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BillModal({ bill, billType, wallets, categories, incomeCategories, saving, onSave, onClose, onUpload }) {
  const isIncome = billType === 'income';
  const relevantCategories = isIncome ? incomeCategories : categories;
  
  const [form, setForm] = useState({
    name: bill?.name || '',
    amount: bill?.amount || '',
    due_date: bill?.due_date || new Date().toISOString().split('T')[0],
    category: bill?.category || relevantCategories[0]?.name || relevantCategories[0],
    icon: bill?.icon || null,
    wallet_id: bill?.wallet_id || '',
    status: bill?.status || 'pending',
    is_recurring: bill?.is_recurring || false,
    recurrence_type: bill?.recurrence_type || 'monthly',
    recurrence_count: 12,
    notes: bill?.notes || '',
    pix_code: bill?.pix_code || '',
    payer: bill?.payer || '', // Quem vai pagar (para receitas)
    attachment_url: bill?.attachment_url || '',
    attachment_name: bill?.attachment_name || '',
    notification_enabled: bill?.notification_enabled || false,
    notification_days_before: bill?.notification_days_before || 3,
    type: billType || bill?.type || 'expense'
  });
  const [uploading, setUploading] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [customCategories, setCustomCategories] = useState([]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true);
      const result = await onUpload(file);
      if (result) {
        setForm({...form, attachment_url: result.url, attachment_name: result.name});
      }
      setUploading(false);
    }
  };

  const handleCategoryChange = (cat) => {
    const catList = isIncome ? incomeCategories : defaultCategories;
    const catObj = catList.find(c => c.name === cat);
    setForm({...form, category: cat, icon: catObj?.icon || 'MoreHorizontal'});
  };

  const addNewCategory = () => {
    if (newCategoryName.trim()) {
      setCustomCategories([...customCategories, newCategoryName.trim()]);
      setForm({...form, category: newCategoryName.trim()});
      setNewCategoryName('');
      setShowNewCategory(false);
    }
  };

  const baseCatNames = (isIncome ? incomeCategories : defaultCategories).map(c => c.name);
  const allCategories = [...baseCatNames, ...customCategories.filter(c => !baseCatNames.includes(c))];

  // Calcular preview das datas que serão geradas
  const getPreviewDates = () => {
    if (!form.is_recurring || !form.due_date) return [];
    
    const dates = [];
    const startDate = new Date(form.due_date + 'T00:00:00');
    
    for (let i = 0; i < Math.min(form.recurrence_count, 6); i++) {
      const newDate = new Date(startDate);
      switch(form.recurrence_type) {
        case 'weekly':
          newDate.setDate(startDate.getDate() + (i * 7));
          break;
        case 'monthly':
          newDate.setMonth(startDate.getMonth() + i);
          break;
        case 'quarterly':
          newDate.setMonth(startDate.getMonth() + (i * 3));
          break;
        case 'yearly':
          newDate.setFullYear(startDate.getFullYear() + i);
          break;
        default:
          break;
      }
      dates.push(newDate);
    }
    return dates;
  };

  const previewDates = getPreviewDates();
  const isEditing = !!bill;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className={`p-6 border-b border-slate-700/50 flex items-center justify-between sticky top-0 rounded-t-2xl ${isIncome ? 'bg-green-900/50' : 'bg-slate-900'}`}>
          <div className="flex items-center gap-3">
            {isIncome ? <TrendingUp className="w-6 h-6 text-green-400" /> : <TrendingDown className="w-6 h-6 text-red-400" />}
            <h2 className="text-xl font-semibold text-white">
              {isEditing ? `Editar ${isIncome ? 'Receita' : 'Despesa'}` : `Nova ${isIncome ? 'Receita' : 'Despesa'}`}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">{isIncome ? 'Descrição *' : 'Nome da Conta *'}</label>
            <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all" placeholder={isIncome ? 'Ex: Pagamento Freelance' : 'Ex: Conta de Luz'} required />
          </div>

          {/* Campo Pagador - só para receitas */}
          {isIncome && (
            <div>
              <label className="block text-sm text-slate-400 mb-2">Quem vai pagar?</label>
              <div className="relative">
                <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input type="text" value={form.payer} onChange={(e) => setForm({...form, payer: e.target.value})} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all" placeholder="Ex: João Silva, Empresa XYZ" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Valor *</label>
              <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all" placeholder="0,00" required />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">{isIncome ? 'Data Prevista *' : 'Vencimento *'}</label>
              <input type="date" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-all" required />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Categoria</label>
              <div className="relative">
                <select 
                  value={form.category} 
                  onChange={(e) => handleCategoryChange(e.target.value)} 
                  className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-all"
                >
                  {allCategories.map(cat => {
                    return <option key={cat} value={cat}>{cat}</option>;
                  })}
                </select>
              </div>
              <button 
                type="button"
                onClick={() => setShowNewCategory(!showNewCategory)}
                className="mt-2 text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Adicionar nova categoria
              </button>
              
              {showNewCategory && (
                <div className="mt-2 flex gap-2">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Nome da categoria"
                    className="flex-1 bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={addNewCategory}
                    className="px-3 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 text-sm"
                  >
                    Adicionar
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Carteira</label>
              <select value={form.wallet_id} onChange={(e) => setForm({...form, wallet_id: e.target.value})} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-all">
                <option value="">Nenhuma</option>
                {wallets.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          </div>

          {/* Seletor de Ícone */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Ícone da Conta</label>
            <button
              type="button"
              onClick={() => setShowIconPicker(!showIconPicker)}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-all flex items-center gap-3"
            >
              {(() => {
                const IconComponent = getCategoryIcon(form.category, form.icon);
                return <IconComponent className="w-5 h-5 text-emerald-400" />;
              })()}
              <span>Clique para escolher um ícone</span>
            </button>
            
            {showIconPicker && (
              <div className="mt-2 p-3 bg-slate-800/50 border border-slate-700 rounded-xl">
                <p className="text-xs text-slate-400 mb-2">Escolha um ícone:</p>
                <div className="grid grid-cols-8 gap-2 max-h-32 overflow-y-auto">
                  {Object.entries(availableIcons).map(([name, Icon]) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => {
                        setForm({...form, icon: name});
                        setShowIconPicker(false);
                      }}
                      className={`p-2 rounded-lg transition-all ${form.icon === name ? 'bg-emerald-500/30 text-emerald-400' : 'bg-slate-700/30 text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                      title={name}
                    >
                      <Icon className="w-4 h-4" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Campo PIX */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Código PIX (opcional)</label>
            <textarea 
              value={form.pix_code} 
              onChange={(e) => setForm({...form, pix_code: e.target.value})} 
              className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all h-20 resize-none font-mono text-sm" 
              placeholder="Cole aqui o código PIX copia e cola..."
            />
          </div>

          {/* Notificação Individual */}
          <div className="p-4 bg-slate-800/30 rounded-xl space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium flex items-center gap-2">
                  <Bell className="w-4 h-4 text-emerald-400" />
                  Lembrete Push
                </p>
                <p className="text-slate-500 text-sm">Receber notificação antes do vencimento</p>
              </div>
              <button type="button" onClick={() => setForm({...form, notification_enabled: !form.notification_enabled})} className={`w-12 h-6 rounded-full transition-all ${form.notification_enabled ? 'bg-emerald-500' : 'bg-slate-600'}`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-all ${form.notification_enabled ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
              </button>
            </div>

            {form.notification_enabled && (
              <div>
                <label className="block text-sm text-slate-400 mb-2">Notificar com antecedência de</label>
                <select value={form.notification_days_before} onChange={(e) => setForm({...form, notification_days_before: parseInt(e.target.value)})} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-all">
                  <option value={1}>1 dia antes</option>
                  <option value={2}>2 dias antes</option>
                  <option value={3}>3 dias antes</option>
                  <option value={5}>5 dias antes</option>
                  <option value={7}>7 dias antes</option>
                  <option value={10}>10 dias antes</option>
                  <option value={15}>15 dias antes</option>
                  <option value={30}>30 dias antes</option>
                </select>
              </div>
            )}
          </div>

          {/* Recorrência - só mostra se NÃO estiver editando */}
          {!isEditing && (
            <div className="p-4 bg-slate-800/30 rounded-xl space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Conta Recorrente</p>
                  <p className="text-slate-500 text-sm">Gera várias contas de uma vez</p>
                </div>
                <button type="button" onClick={() => setForm({...form, is_recurring: !form.is_recurring})} className={`w-12 h-6 rounded-full transition-all ${form.is_recurring ? 'bg-emerald-500' : 'bg-slate-600'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-all ${form.is_recurring ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                </button>
              </div>

              {form.is_recurring && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Frequência</label>
                      <select value={form.recurrence_type} onChange={(e) => setForm({...form, recurrence_type: e.target.value})} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-all">
                        <option value="weekly">Semanal</option>
                        <option value="monthly">Mensal</option>
                        <option value="quarterly">Trimestral</option>
                        <option value="yearly">Anual</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Repetir por</label>
                      <select value={form.recurrence_count} onChange={(e) => setForm({...form, recurrence_count: parseInt(e.target.value)})} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-all">
                        <option value={2}>2 vezes</option>
                        <option value={3}>3 vezes</option>
                        <option value={6}>6 vezes</option>
                        <option value={12}>12 vezes</option>
                        <option value={24}>24 vezes</option>
                        <option value={36}>36 vezes</option>
                      </select>
                    </div>
                  </div>

                  {/* Preview das datas */}
                  <div className="p-3 bg-slate-700/30 rounded-lg">
                    <p className="text-xs text-slate-400 mb-2">📅 Serão criadas {form.recurrence_count} contas:</p>
                    <div className="flex flex-wrap gap-2">
                      {previewDates.map((date, idx) => (
                        <span key={idx} className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">
                          {date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })}
                        </span>
                      ))}
                      {form.recurrence_count > 6 && (
                        <span className="text-xs text-slate-500">+{form.recurrence_count - 6} mais...</span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm text-slate-400 mb-2">Observações</label>
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all h-20 resize-none" placeholder="Anotações..." />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Anexo (Boleto)</label>
            <div className="border-2 border-dashed border-slate-700 rounded-xl p-4 text-center hover:border-emerald-500/50 transition-all">
              <input type="file" onChange={handleFileChange} className="hidden" id="fileInput" accept=".pdf,.png,.jpg,.jpeg" />
              <label htmlFor="fileInput" className="cursor-pointer">
                {uploading ? (
                  <div className="flex items-center justify-center gap-2 text-emerald-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Enviando...</span>
                  </div>
                ) : form.attachment_name ? (
                  <div className="flex items-center justify-center gap-2 text-emerald-400">
                    <FileText className="w-5 h-5" />
                    <span>{form.attachment_name}</span>
                    <button type="button" onClick={(e) => { e.preventDefault(); setForm({...form, attachment_url: '', attachment_name: ''}); }} className="p-1 hover:text-red-400">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="text-slate-500">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p>Clique para anexar arquivo</p>
                    <p className="text-xs">PDF, PNG, JPG</p>
                  </div>
                )}
              </label>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-slate-700/50 flex gap-3 justify-end sticky bottom-0 bg-slate-900 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white transition-all">Cancelar</button>
          <button
            onClick={() => {
              if (!form.name || !form.amount || !form.due_date) {
                alert('Preencha os campos obrigatórios');
                return;
              }
              onSave(form);
            }}
            disabled={saving}
            className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-2 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            <Save className="w-4 h-4" />
            {isEditing ? 'Salvar Alterações' : (form.is_recurring ? `Criar ${form.recurrence_count} contas` : 'Criar Conta')}
          </button>
        </div>
      </div>
    </div>
  );
}

function WalletModal({ wallet, saving, onSave, onClose }) {
  const [form, setForm] = useState({
    name: wallet?.name || '',
    description: wallet?.description || '',
    color: wallet?.color || '#10b981'
  });

  const colors = ['#10b981', '#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308'];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md">
        <div className="p-6 border-b border-slate-700/50 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">{wallet ? 'Editar Carteira' : 'Nova Carteira'}</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Nome *</label>
            <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all" placeholder="Ex: Nubank" required />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Descrição</label>
            <input type="text" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all" placeholder="Ex: Conta corrente" />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Cor</label>
            <div className="flex gap-2 flex-wrap">
              {colors.map(color => (
                <button key={color} type="button" onClick={() => setForm({...form, color})} className={`w-10 h-10 rounded-xl transition-all ${form.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-900' : ''}`} style={{ backgroundColor: color }} />
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-slate-700/50 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white transition-all">Cancelar</button>
          <button
            onClick={() => {
              if (!form.name) { alert('Preencha o nome'); return; }
              onSave(form);
            }}
            disabled={saving}
            className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-2 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            <Save className="w-4 h-4" />
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function PaymentModal({ bill, onConfirm, onClose, onUpload }) {
  const [receiptUrl, setReceiptUrl] = useState('');
  const [receiptName, setReceiptName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true);
      const result = await onUpload(file);
      if (result) {
        setReceiptUrl(result.url);
        setReceiptName(result.name);
      }
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    await onConfirm(bill.id, receiptUrl, receiptName);
    setConfirming(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md">
        <div className="p-6 border-b border-slate-700/50 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">Confirmar Pagamento</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="p-4 bg-slate-800/50 rounded-xl">
            <p className="text-slate-400 text-sm">Conta</p>
            <p className="text-white font-semibold text-lg">{bill.name}</p>
            <p className="text-emerald-400 font-bold text-2xl mt-2">{formatCurrency(bill.amount)}</p>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Comprovante (opcional)</label>
            <div className="border-2 border-dashed border-slate-700 rounded-xl p-4 text-center hover:border-emerald-500/50 transition-all">
              <input type="file" onChange={handleFileChange} className="hidden" id="receiptInput" accept=".pdf,.png,.jpg,.jpeg" />
              <label htmlFor="receiptInput" className="cursor-pointer">
                {uploading ? (
                  <div className="flex items-center justify-center gap-2 text-emerald-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Enviando...</span>
                  </div>
                ) : receiptName ? (
                  <div className="flex items-center justify-center gap-2 text-emerald-400">
                    <Image className="w-5 h-5" />
                    <span>{receiptName}</span>
                    <button type="button" onClick={(e) => { e.preventDefault(); setReceiptUrl(''); setReceiptName(''); }} className="p-1 hover:text-red-400">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="text-slate-500">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p>Anexar comprovante</p>
                    <p className="text-xs">PDF, PNG, JPG</p>
                  </div>
                )}
              </label>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-slate-700/50 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white transition-all">Cancelar</button>
          <button
            onClick={handleConfirm}
            disabled={confirming}
            className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-2 rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all disabled:opacity-50"
          >
            {confirming && <Loader2 className="w-4 h-4 animate-spin" />}
            <CheckCircle className="w-4 h-4" />
            Confirmar Pagamento
          </button>
        </div>
      </div>
    </div>
  );
}